import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/env';
import { useAuth } from '../context/AuthContext';
import { userHasCrmPermission } from '../utils/crmPermissions';
import { buildCrmSwalBody, fireConfirmDialog, fireCrmAlert } from '../utils/swalConfirm';
import { formatFechaCorta, normalizeFechaIso, resolvePlanDosificacionPedido } from '../utils/tratamientoDosificacion';
import { crearClienteExternoAltaRapida } from '../utils/altaRapidaPedido';
import { fetchConversationTemplates, sendConversationTemplate } from '../api/chatwoot';
import '../styles/alta-rapida-pedido.css';
import '../styles/private-recetas-router-premium.css';

function splitMedicamentoOption(m) {
  const clean = (v) => {
    const s = String(v ?? '').trim();
    if (!s) return '';
    const low = s.toLowerCase();
    if (low === 'null' || low === 'undefined' || low === '0' || low === '0.0') return '';
    return s;
  };
  const generico = clean(m?.nombre_generico) || 'Sin nombre';
  const comercial = clean(m?.nombre_comercial);
  const detalles = [clean(m?.concentracion), clean(m?.presentacion), clean(m?.laboratorio)]
    .filter(Boolean)
    .join(' · ');
  return {
    primary: `${generico}${comercial ? ` · ${comercial}` : ''}`,
    secondary: detalles
  };
}

const POLL_MS = 12000;
const RECETA_EMITIDA_ESTADOS = new Set(['emitida_sistema_externo', 'confirmada_dispensa', 'emitida']);
const NEW_TREATMENT_INITIAL = {
  genericDrug: '',
  commercialName: '',
  presentacion: '',
  medicamentoId: null,
  diagnosis: '',
  posologia: '',
  startDate: '',
  months: '6',
  destino: 'aprobacion_medica'
};
const NEW_PATIENT_INITIAL = {
  fullName: '',
  dni: '',
  birthDate: '',
  telefono: '',
  email: '',
  nroAfiliado: '',
  obraSocial: '',
  medicoCabecera: '',
  medicoCabeceraId: null,
  genericDrug: '',
  commercialName: '',
  presentacion: '',
  medicamentoId: null,
  diagnosis: '',
  posologia: '',
  startDate: '',
  months: '6',
  destino: 'aprobacion_medica'
};

const MODULES = {
  derivacion: {
    key: 'derivacion',
    title: 'Gestion Derivacion',
    route: '/gestion-derivacion',
    endpoint: '/api/asociacion-recetas/pedidos-derivacion',
    permission: 'ver_gestion_derivacion'
  },
  medico: {
    key: 'medico',
    title: 'Pedidos Recetas Medico',
    route: '/pedidos-recetas-medico',
    endpoint: '/api/asociacion-recetas/pedidos-pendientes',
    permission: 'ver_pedido_recetas_medico'
  },
  emitir: {
    key: 'emitir',
    title: 'Recetas a Emitir',
    route: '/recetas-emitir',
    endpoint: '/api/asociacion-recetas/pedidos',
    permission: 'ver_recetas_emitir'
  }
};

function safeList(value) {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.pedidos)) return value.pedidos;
  if (Array.isArray(value?.data)) return value.data;
  return [];
}

function normalizeText(value) {
  return String(value || '').trim();
}

function todayIso() {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
}

function normalizeDni(value) {
  return String(value || '').replace(/\D/g, '').replace(/^0+/, '');
}

function formatDni(value) {
  const digits = normalizeDni(value);
  if (!digits) return '—';
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function formatDoctorName(nombre) {
  const raw = normalizeText(nombre);
  if (!raw) return '';
  if (/^(dr|dra)\.?\s/i.test(raw)) return raw;
  return `Dr/a. ${raw}`;
}

function resolveMonthsSelectValue(value) {
  const raw = Math.max(Number(value) || 0, 1);
  if ([1, 3, 6, 12].includes(raw)) return String(raw);
  if (raw <= 1) return '1';
  if (raw <= 3) return '3';
  if (raw <= 6) return '6';
  return '12';
}

function resolveDestinoFromModuleKey(moduleKey) {
  return moduleKey === 'derivacion' ? 'derivacion' : 'aprobacion_medica';
}

function resolveObraSocialFromRow(row) {
  return normalizeText(
    row?.obra_social_descripcio
    || row?.obra_social
    || row?.obra_social_geclisa
    || row?.obra_social_plex
    || row?.os_sigla_geclisa
    || ''
  );
}

function resolveMedicoFromRow(row, medicosById = {}) {
  const medicoId = Number(row?.medico_id);
  const id = Number.isFinite(medicoId) && medicoId > 0 ? medicoId : null;
  const inlineName = normalizeText(
    row?.medico || row?.medico_geclisa || row?.prestador_geclisa || row?.prestador_plex || ''
  );
  if (inlineName) return { id, name: inlineName };
  if (id && medicosById[id]) return { id, name: medicosById[id] };
  return { id, name: '' };
}

function normalizeMedicosFromApi(payload) {
  const raw = Array.isArray(payload)
    ? payload
    : (Array.isArray(payload?.medicos) ? payload.medicos : []);
  const cleaned = raw.map((item, idx) => {
    if (typeof item === 'string') {
      const nombre = item.trim();
      return nombre ? { id: `prestador-${idx}-${nombre}`, nombre } : null;
    }
    if (!item || typeof item !== 'object') return null;
    const nombre = String(item.nombre || item.prestador_geclisa || '').trim();
    if (!nombre) return null;
    const id = item.id != null ? String(item.id) : `prestador-${idx}-${nombre}`;
    return { id, nombre, especialidad: item.especialidad || null, matricula: item.matricula || null };
  }).filter(Boolean);

  const uniqueByName = new Map();
  cleaned.forEach((m) => {
    const key = String(m.nombre || '').toLowerCase();
    if (!key || uniqueByName.has(key)) return;
    uniqueByName.set(key, m);
  });
  return Array.from(uniqueByName.values()).sort((a, b) =>
    String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' })
  );
}

function hasModulePermission(user, modulePermission) {
  if (!modulePermission) return true;
  return userHasCrmPermission(user, 'ver_directorio')
    || userHasCrmPermission(user, modulePermission);
}

function statusLabel(raw) {
  const estado = normalizeText(raw).toLowerCase();
  if (estado.includes('pendiente_derivacion')) return 'Pendiente derivacion';
  if (estado.includes('pendiente_aprobacion_medica')) return 'Pendiente medico';
  if (estado.includes('aprobado')) return 'Aprobado';
  if (estado.includes('rechazado')) return 'Rechazado';
  if (estado.includes('en_tratamiento')) return 'En tratamiento';
  if (estado.includes('pendiente')) return 'Pendiente emision';
  return normalizeText(raw) || 'Pendiente';
}

function statusPillClass(status) {
  const s = normalizeText(status).toLowerCase();
  if (s.includes('aprobado')) return 'crm-gd-estado-pill crm-gd-estado-pill--aprobado';
  if (s.includes('rechazado')) return 'crm-gd-estado-pill crm-gd-estado-pill--rechazado';
  if (s.includes('medico')) return 'crm-gd-estado-pill crm-gd-estado-pill--medico';
  if (s.includes('tratamiento')) return 'crm-gd-estado-pill crm-gd-estado-pill--tratamiento';
  if (s.includes('derivacion')) return 'crm-gd-estado-pill crm-gd-estado-pill--derivacion';
  return 'crm-gd-estado-pill crm-gd-estado-pill--muted';
}

function statusBadge(status) {
  const s = normalizeText(status).toLowerCase();
  if (s.includes('aprobado')) return { tone: 'aprobado', icon: '✓' };
  if (s.includes('rechazado')) return { tone: 'rechazado', icon: '✕' };
  if (s.includes('medico')) return { tone: 'medico', icon: '⏱' };
  if (s.includes('tratamiento')) return { tone: 'tratamiento', icon: '✚' };
  if (s.includes('derivacion')) return { tone: 'derivacion', icon: '↗' };
  if (s.includes('emision') || s.includes('pendiente')) return { tone: 'muted', icon: '•' };
  return { tone: 'muted', icon: '•' };
}

function patientKeyFromRow(row) {
  const dni = normalizeDni(row?.dni || row?.paciente_dni);
  if (dni) return `dni:${dni}`;
  const byName = normalizeText(row?.paciente || row?.nombre_paciente || row?.nombre).toLowerCase();
  if (byName) return `name:${byName}`;
  return `id:${row?.id || row?.pedido_id || Math.random()}`;
}

function resolvePatientName(row) {
  const direct = normalizeText(
    row?.paciente
    || row?.nombre_paciente
    || row?.nombre
    || row?.paciente_nombre
  );
  if (direct) return direct;

  const geclisaNombre = normalizeText(row?.nombre_geclisa);
  const geclisaApellido = normalizeText(row?.apellido_geclisa);
  const geclisaFull = [geclisaApellido, geclisaNombre].filter(Boolean).join(', ');
  if (geclisaFull) return geclisaFull;

  const plex = normalizeText(row?.nombre_plex || row?.nombre_completo);
  if (plex) return plex;

  const dni = normalizeDni(row?.dni || row?.paciente_dni || row?.solicitante_dni);
  if (dni) return `Paciente DNI ${formatDni(dni)}`;
  return 'Paciente sin nombre';
}

function inlineMedName(row) {
  return normalizeText(
    row?.medicamento_nombre
    || row?.medicamento
    || row?.nombre_medicamento
    || row?.nombre_comercial
    || row?.producto
  );
}

function getMedication(row, medById = {}) {
  const inline = inlineMedName(row);
  if (inline && inline.toLowerCase() !== 'cargando medicamento...') return inline;
  const mid = Number(row?.medicamento_id);
  const detail = Number.isFinite(mid) ? medById[mid] : null;
  if (detail?.label) return detail.label;
  return 'Sin detalle de medicamento';
}

function cleanField(value) {
  const s = normalizeText(value);
  if (!s) return '';
  const low = s.toLowerCase();
  if (low === 'null' || low === 'undefined' || low === '0' || low === '0.0') return '';
  return s;
}

function buildMedDetails(row, medById = {}) {
  const mid = Number(row?.medicamento_id);
  const detail = (Number.isFinite(mid) && medById[mid]) ? medById[mid] : {};
  return {
    generico: cleanField(row?.nombre_generico || row?.medicamento_generico || detail.generico),
    comercial: cleanField(row?.nombre_comercial || detail.comercial),
    concentracion: cleanField(row?.concentracion || row?.medicamento_concentracion || detail.concentracion),
    presentacion: cleanField(row?.presentacion || row?.medicamento_presentacion || detail.presentacion),
    laboratorio: cleanField(row?.laboratorio || row?.medicamento_laboratorio || detail.laboratorio)
  };
}

function getDiagnostico(row) {
  return normalizeText(row?.diagnostico || row?.descripcion || row?.notas || '');
}

function shouldIncludeEmision(row) {
  const estado = normalizeText(row?.estado).toLowerCase();
  return estado === 'aprobado' || estado === 'en_tratamiento';
}

function pedidoRowId(row) {
  const id = row?.id ?? row?.pedido_id;
  return id != null ? String(id) : null;
}

function dedupePedidoRows(emitirRows, medicoRows, derivacionRows) {
  const seen = new Set();
  const out = [];
  const push = (row, moduleKey) => {
    if (!row) return;
    const pid = pedidoRowId(row);
    if (pid) {
      if (seen.has(pid)) return;
      seen.add(pid);
    }
    out.push({ ...row, _module: moduleKey });
  };
  (emitirRows || []).forEach((row) => push(row, MODULES.emitir.key));
  (medicoRows || []).forEach((row) => push(row, MODULES.medico.key));
  (derivacionRows || []).forEach((row) => push(row, MODULES.derivacion.key));
  return out;
}

function isTxAprobado(status) {
  const s = normalizeText(status).toLowerCase();
  return s.includes('aprobado') || s.includes('tratamiento');
}

function isTxRechazado(status) {
  return normalizeText(status).toLowerCase().includes('rechaz');
}

function isTxPendiente(status) {
  return normalizeText(status).toLowerCase().includes('pendiente');
}

function isTxAprobable(status) {
  const s = normalizeText(status).toLowerCase();
  return s.includes('pendiente_aprobacion_medica') || s.includes('rechaz');
}

function resolveCantidadRecetas(pedido) {
  const cantidad = Number(pedido?.cantidad_recetas);
  const periodo = Number(pedido?.periodo_meses);
  const fromCantidad = Number.isFinite(cantidad) && cantidad > 0 ? cantidad : 0;
  const fromPeriodo = Number.isFinite(periodo) && periodo > 0 ? periodo : 0;
  return Math.max(fromCantidad, fromPeriodo, 1);
}

function isRecetaEstadoEmitida(estado) {
  const s = String(estado || '').trim().toLowerCase();
  return RECETA_EMITIDA_ESTADOS.has(s) || s.includes('emitid');
}

function parsePedidoOrigenFromNotas(notas) {
  const raw = String(notas || '');
  const match = raw.match(
    /^\[\[origen:([a-z0-9_]+)(?:;conv:([0-9]+))?(?:;fin:([^;\]]+))?\]\]\s*/i
  );
  if (!match) {
    return {
      ingreso_origen: null,
      ingreso_conversation_id: null,
      ingreso_finalized_at: null,
      notas_display: raw.trim()
    };
  }
  return {
    ingreso_origen: match[1],
    ingreso_conversation_id: match[2] || null,
    ingreso_finalized_at: match[3] || null,
    notas_display: raw.slice(match[0].length).trim()
  };
}

function recetaEstadoMergeRank(estado) {
  if (isRecetaEstadoEmitida(estado)) return 0;
  const s = String(estado || '').trim().toLowerCase();
  if (s === 'pendiente') return 2;
  return 1;
}

function pickPersistedRecetaMatch(persisted, numero) {
  const matches = (persisted || []).filter((pr) => Number(pr.numero_receta) === Number(numero));
  if (!matches.length) return null;
  return matches.slice().sort((a, b) => {
    const rankDiff = recetaEstadoMergeRank(a.estado) - recetaEstadoMergeRank(b.estado);
    if (rankDiff !== 0) return rankDiff;
    return Number(b.id || 0) - Number(a.id || 0);
  })[0];
}

function addMonthsToIso(isoDate, monthsToAdd) {
  const base = normalizeFechaIso(isoDate);
  if (!base) return null;
  const d = new Date(`${base}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  d.setMonth(d.getMonth() + monthsToAdd);
  return d.toISOString().split('T')[0];
}

function formatMonthShortLabel(isoDate) {
  const base = normalizeFechaIso(isoDate);
  if (!base) return '';
  const d = new Date(`${base}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  const month = d.toLocaleDateString('es-AR', { month: 'short' }).replace(/\./g, '');
  const year = String(d.getFullYear()).slice(-2);
  return `${month} ${year}`;
}

function resolveSecuencialEmisionBlock(timeline, rx) {
  const numeroActual = Number(rx?.numero);
  if (!Number.isFinite(numeroActual) || numeroActual <= 1) return null;
  const prevPendiente = (timeline || []).some((item) => {
    const n = Number(item?.numero);
    if (!Number.isFinite(n) || n >= numeroActual) return false;
    return !item.emitted;
  });
  if (!prevPendiente) return null;
  const firstPendiente = (timeline || [])
    .filter((item) => !item.emitted)
    .map((item) => Number(item.numero))
    .filter((n) => Number.isFinite(n))
    .sort((a, b) => a - b)[0];
  return `Emisión secuencial: primero emití R${firstPendiente || numeroActual - 1}.`;
}

function isValidHttpUrl(value) {
  try {
    const url = new URL(String(value || '').trim());
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch (_) {
    return false;
  }
}

function buildRecetaSlotDates(pedido, total) {
  const fechaInicio = normalizeFechaIso(pedido?.fecha_inicio || pedido?.fecha_solicitud);
  const plan = resolvePlanDosificacionPedido(pedido);
  const slots = [];
  for (let idx = 0; idx < total; idx += 1) {
    const numero = idx + 1;
    const planSlot = plan.slots[idx];
    let fechaPrevista = planSlot?.fechaPrevista || '';
    if (!fechaPrevista && fechaInicio) {
      fechaPrevista = addMonthsToIso(fechaInicio, idx) || '';
    }
    slots.push({ numero, fechaPrevista });
  }
  return slots;
}

function buildTreatmentTimeline(row, recetasByPedidoId) {
  const estado = normalizeText(row?.estado).toLowerCase();
  if (estado.includes('cancelad')) return [];
  return timelineFromRecetas(row, recetasByPedidoId[String(row.id)] || []);
}

function timelineFromRecetas(pedido, recetas) {
  const total = resolveCantidadRecetas(pedido);
  const persisted = Array.isArray(recetas) ? recetas : [];
  const pedidoId = pedido?.id || pedido?.pedido_id;
  const slots = buildRecetaSlotDates(pedido, total);
  return slots.map(({ numero, fechaPrevista }) => {
    const match = pickPersistedRecetaMatch(persisted, numero);
    const emitted = isRecetaEstadoEmitida(match?.estado) || Boolean(
      match?.fecha_emitida || match?.nro_receta_externo
    );
    return {
      id: `${pedidoId}-${numero}`,
      numero,
      label: `R${numero}`,
      monthLabel: formatMonthShortLabel(fechaPrevista),
      fechaPrevista,
      estado: emitted ? (match?.estado || 'emitida_sistema_externo') : 'pendiente',
      emitted,
      nroExterno: match?.nro_receta_externo || '',
      recetaUrl: match?.receta_url || ''
    };
  });
}

async function fetchJson(path) {
  const res = await fetch(`${API_BASE_URL}${path}`, { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function withDirectorioListQuery(path) {
  const base = String(path || '');
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}directorio=1`;
}

const RR_CHECK_SVG = `
  <svg viewBox="0 0 52 52" aria-hidden="true">
    <circle class="crm-rr-swal__ring" cx="26" cy="26" r="23" />
    <path class="crm-rr-swal__tick" d="M16 27.5l7 7 14-15" />
  </svg>
`;

const RR_X_SVG = `
  <svg viewBox="0 0 52 52" aria-hidden="true">
    <circle class="crm-rr-swal__ring" cx="26" cy="26" r="23" />
    <path class="crm-rr-swal__tick" d="M18 18l16 16 M34 18l-16 16" />
  </svg>
`;

const RR_BANG_SVG = `
  <svg viewBox="0 0 52 52" aria-hidden="true">
    <circle class="crm-rr-swal__ring" cx="26" cy="26" r="23" />
    <path class="crm-rr-swal__tick" d="M26 15v15" />
    <path class="crm-rr-swal__tick crm-rr-swal__tick--dot" d="M26 37.4v0.1" />
  </svg>
`;

const RR_EYE_SVG = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2.2 12S5.6 5.5 12 5.5 21.8 12 21.8 12 18.4 18.5 12 18.5 2.2 12 2.2 12Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const RR_DX_SVG = (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 4h6a1 1 0 0 1 1 1v1h1.5A1.5 1.5 0 0 1 19 7.5V19a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 19V7.5A1.5 1.5 0 0 1 6.5 6H8V5a1 1 0 0 1 1-1Z" />
    <path d="M9 4.5h6V7H9z" />
    <path d="M8.5 11h7M8.5 14.5h5" />
  </svg>
);

const RR_ACT_CHECK = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7" />
  </svg>
);

const RR_ACT_X = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

const RR_ACT_PENCIL = (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14.5 5.5l4 4M4 20l1-4L16 5a1.5 1.5 0 0 1 2.1 0l.9.9a1.5 1.5 0 0 1 0 2.1L8 19l-4 1Z" />
  </svg>
);

/** Cuerpo premium para alertas de éxito (badge animado + chip de WhatsApp opcional). */
function buildRrSuccessBody({ title = '', message = '', wa = null, tone = 'success' } = {}) {
  const safeTitle = String(title || '');
  const safeMessage = String(message || '');
  let chip = '';
  if (wa === 'sent') {
    chip = `
      <div class="crm-rr-swal__chip crm-rr-swal__chip--ok">
        <span class="crm-rr-swal__chip-ic" aria-hidden="true">💬</span>
        WhatsApp enviado al paciente
      </div>`;
  } else if (wa === 'failed') {
    chip = `
      <div class="crm-rr-swal__chip crm-rr-swal__chip--warn">
        <span class="crm-rr-swal__chip-ic" aria-hidden="true">⚠️</span>
        El WhatsApp no pudo enviarse
      </div>`;
  }
  const isDanger = tone === 'danger';
  const badgeMod = isDanger ? ' crm-rr-swal__badge--danger' : (wa === 'failed' ? ' is-warn' : '');
  return `
    <div class="crm-rr-swal">
      <div class="crm-rr-swal__badge${badgeMod}">${isDanger ? RR_X_SVG : RR_CHECK_SVG}</div>
      <div class="crm-rr-swal__title">${safeTitle}</div>
      <div class="crm-rr-swal__msg">${safeMessage}</div>
      ${chip}
    </div>
  `;
}

/** Cuerpo premium para confirmaciones (badge de advertencia + lista de consecuencias). */
function buildRrConfirmBody({ title = '', message = '', items = [], tone = 'danger' } = {}) {
  const safeTitle = String(title || '');
  const safeMessage = String(message || '');
  const list = Array.isArray(items) && items.length
    ? `<ul class="crm-rr-swal__list">${items.map((i) => `<li>${i}</li>`).join('')}</ul>`
    : '';
  const badgeMod = tone === 'danger' ? ' crm-rr-swal__badge--danger' : ' is-warn';
  return `
    <div class="crm-rr-swal crm-rr-swal--confirm">
      <div class="crm-rr-swal__badge${badgeMod}">${RR_BANG_SVG}</div>
      <div class="crm-rr-swal__title">${safeTitle}</div>
      <div class="crm-rr-swal__msg">${safeMessage}</div>
      ${list}
    </div>
  `;
}

export default function PrivateRecetasRouter() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [omnicanal, setOmnicanal] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedPatientKey, setSelectedPatientKey] = useState(null);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [txViewMode, setTxViewMode] = useState('rows');
  const [txPageSize, setTxPageSize] = useState(5);
  const [txPage, setTxPage] = useState(1);
  const [txSearch, setTxSearch] = useState('');
  const [txStatusFilter, setTxStatusFilter] = useState('all');
  const [txSourceFilter, setTxSourceFilter] = useState('all');
  const [txFlowFilter, setTxFlowFilter] = useState('all');
  const [txSortMode, setTxSortMode] = useState('fecha_desc');
  const [txDateFrom, setTxDateFrom] = useState('');
  const [txDateTo, setTxDateTo] = useState('');
  const [approveModal, setApproveModal] = useState(null);
  const [approveWa, setApproveWa] = useState(false);
  const [approveStep, setApproveStep] = useState('whatsapp');
  const [processing, setProcessing] = useState(null);
  const [infoPop, setInfoPop] = useState(null);
  const [patientMetaOverrides, setPatientMetaOverrides] = useState({});
  const [editingPatientMeta, setEditingPatientMeta] = useState(false);
  const [metaDraft, setMetaDraft] = useState({ medico: '', medicoId: null, obraSocial: '' });
  const [metaPicker, setMetaPicker] = useState(null);
  const [metaQuery, setMetaQuery] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);
  const [rowsByModule, setRowsByModule] = useState({ derivacion: [], medico: [], emitir: [] });
  const [recetasByPedidoId, setRecetasByPedidoId] = useState({});
  const [medById, setMedById] = useState({});
  const medByIdRef = useRef({});
  const [customPatients] = useState([]);
  const [manualTreatmentsByPatient] = useState({});
  const [newTreatmentForm, setNewTreatmentForm] = useState(NEW_TREATMENT_INITIAL);
  const [isNewTreatmentOpen, setIsNewTreatmentOpen] = useState(false);
  const [isNewPatientOpen, setIsNewPatientOpen] = useState(false);
  const [newPatientStep, setNewPatientStep] = useState('dni');
  const [savingAltaPaciente, setSavingAltaPaciente] = useState(false);
  const [newPatientForm, setNewPatientForm] = useState(NEW_PATIENT_INITIAL);
  const [medSearchOpen, setMedSearchOpen] = useState(false);
  const [medSearchField, setMedSearchField] = useState('newTreatment:genericDrug');
  const [medSearchQuery, setMedSearchQuery] = useState('');
  const [medOptions, setMedOptions] = useState([]);
  const [medSearchLoading, setMedSearchLoading] = useState(false);
  const [medicosApi, setMedicosApi] = useState([]);
  const [medicosLoading, setMedicosLoading] = useState(true);
  const [obrasSocialesApi, setObrasSocialesApi] = useState([]);
  const [obrasSocialesLoading, setObrasSocialesLoading] = useState(true);
  const [dniMatch, setDniMatch] = useState(null);
  const [dniMatchLoading, setDniMatchLoading] = useState(false);
  const [afilPicker, setAfilPicker] = useState(null);
  const [afilQuery, setAfilQuery] = useState('');
  const [txOverrides, setTxOverrides] = useState({});
  const [savingPatient, setSavingPatient] = useState(false);
  const [savingTreatment, setSavingTreatment] = useState(false);
  const [editingPedido, setEditingPedido] = useState(null);
  const [pedidoActionId, setPedidoActionId] = useState(null);
  const [rxBlockedNotice, setRxBlockedNotice] = useState(null);
  const [emitModal, setEmitModal] = useState(null);
  const [emitirNro, setEmitirNro] = useState('');
  const [emitirUrl, setEmitirUrl] = useState('');
  const [emitWa, setEmitWa] = useState(false);
  const [emitStep, setEmitStep] = useState('whatsapp');
  const [confirmandoEmision, setConfirmandoEmision] = useState(false);

  // Estados para modal de plantillas de WhatsApp
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateConversationId, setTemplateConversationId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParamValues, setTemplateParamValues] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [sendingTemplate, setSendingTemplate] = useState(false);

  const medSearchRef = useRef(null);
  const treatmentDrawerRef = useRef(null);
  const rxNoticeTimerRef = useRef(null);

  const { user } = useAuth();

  const activeModules = useMemo(
    () => Object.values(MODULES).filter((m) => hasModulePermission(user, m.permission)),
    [user]
  );

  const loadData = useCallback(async ({ silent = false } = {}) => {
    try {
      if (!silent) setError('');
      const moduleRows = await Promise.all(
        activeModules.map(async (m) => {
          const payload = await fetchJson(withDirectorioListQuery(m.endpoint));
          return [m.key, safeList(payload)];
        })
      );

      const next = { derivacion: [], medico: [], emitir: [] };
      moduleRows.forEach(([key, rows]) => {
        next[key] = rows;
      });
      setRowsByModule(next);

      const allLoadedRows = [...next.derivacion, ...next.medico, ...next.emitir];
      const idsToHydrate = Array.from(new Set(
        allLoadedRows
          .filter((r) => !inlineMedName(r) && Number(r?.medicamento_id) > 0)
          .map((r) => Number(r.medicamento_id))
          .filter((id) => !medByIdRef.current[id])
      ));
      if (idsToHydrate.length) {
        const entries = await Promise.all(idsToHydrate.map(async (mid) => {
          try {
            const jm = await fetchJson(`/api/asociacion-recetas/medicamento/${mid}`);
            const comercial = cleanField(jm?.nombre_comercial);
            const generico = cleanField(jm?.nombre_generico);
            const label = comercial || generico || cleanField(jm?.nombre);
            return [mid, {
              label,
              comercial,
              generico,
              concentracion: cleanField(jm?.concentracion),
              presentacion: cleanField(jm?.presentacion),
              laboratorio: cleanField(jm?.laboratorio)
            }];
          } catch (_) {
            return [mid, null];
          }
        }));
        const additions = {};
        entries.forEach(([mid, detail]) => { if (detail && detail.label) additions[mid] = detail; });
        if (Object.keys(additions).length) {
          medByIdRef.current = { ...medByIdRef.current, ...additions };
          setMedById((prev) => ({ ...prev, ...additions }));
        }
      }

      const recetaPedidoIds = Array.from(new Set(
        allLoadedRows
          .filter(shouldIncludeEmision)
          .map((row) => Number(row.id))
          .filter((id) => Number.isFinite(id) && id > 0)
      ));
      const recipePairs = await Promise.all(
        recetaPedidoIds.map(async (pedidoId) => {
          try {
            const payload = await fetchJson(`/api/asociacion-recetas/pedidos/${pedidoId}/recetas`);
            const recetas = Array.isArray(payload)
              ? payload
              : (Array.isArray(payload?.recetas) ? payload.recetas : safeList(payload));
            return [String(pedidoId), recetas];
          } catch (_) {
            return [String(pedidoId), []];
          }
        })
      );
      setRecetasByPedidoId(Object.fromEntries(recipePairs));
      return true;
    } catch (_) {
      if (!silent) setError('No se pudo cargar el router de recetas.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [activeModules]);

  const closeTemplateModal = useCallback(() => {
    setTemplateModalOpen(false);
    setSelectedTemplate(null);
    setTemplateParamValues([]);
    setTemplatesError('');
    setTemplateConversationId(null);
  }, []);

  const openTemplatePicker = useCallback(async (conversationId) => {
    if (!conversationId) return;
    setTemplateConversationId(conversationId);
    setTemplateModalOpen(true);
    setSelectedTemplate(null);
    setTemplateParamValues([]);
    setTemplatesError('');
    setTemplatesLoading(true);
    try {
      const data = await fetchConversationTemplates(conversationId);
      setTemplates(Array.isArray(data?.templates) ? data.templates : []);
    } catch (err) {
      setTemplatesError(err?.message || 'No se pudieron cargar las plantillas');
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  const handlePickTemplate = useCallback((tpl) => {
    setSelectedTemplate(tpl);
    const count = Number(tpl?.params_count) || 0;
    setTemplateParamValues(Array.from({ length: count }, () => ''));
  }, []);

  const fillTemplateBody = useCallback((body, values) => {
    return String(body || '').replace(/\{\{\s*(\d+)\s*\}\}/g, (match, num) => {
      const idx = Number(num) - 1;
      const val = values?.[idx];
      return val != null && String(val).trim() ? String(val) : match;
    });
  }, []);

  const handleSendTemplate = useCallback(async () => {
    if (!templateConversationId || !selectedTemplate || sendingTemplate) return;
    const count = Number(selectedTemplate.params_count) || 0;
    if (count > 0 && templateParamValues.some((v) => !String(v || '').trim())) {
      setTemplatesError('Completá todos los campos de la plantilla.');
      return;
    }
    const processedParams = {};
    for (let i = 0; i < count; i += 1) {
      processedParams[String(i + 1)] = String(templateParamValues[i] || '');
    }
    const content = fillTemplateBody(selectedTemplate.body, templateParamValues);
    setSendingTemplate(true);
    setTemplatesError('');
    try {
      await sendConversationTemplate(templateConversationId, {
        name: selectedTemplate.name,
        language: selectedTemplate.language,
        category: selectedTemplate.category || undefined,
        namespace: selectedTemplate.namespace || undefined,
        processed_params: processedParams,
        content
      });
      closeTemplateModal();
    } catch (err) {
      setTemplatesError(err?.message || 'No se pudo enviar la plantilla');
    } finally {
      setSendingTemplate(false);
    }
  }, [
    templateConversationId,
    selectedTemplate,
    sendingTemplate,
    templateParamValues,
    fillTemplateBody,
    closeTemplateModal
  ]);

  useEffect(() => {
    loadData();
    const timer = setInterval(() => loadData({ silent: true }), POLL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [loadData]);

  const patientDirectory = useMemo(() => {
    const medicosById = {};
    medicosApi.forEach((m) => {
      const mid = Number(m?.id);
      const nombre = normalizeText(m?.nombre);
      if (Number.isFinite(mid) && mid > 0 && nombre) medicosById[mid] = nombre;
    });

    const allRows = dedupePedidoRows(
      rowsByModule.emitir,
      rowsByModule.medico,
      rowsByModule.derivacion
    );

    const map = new Map();
    allRows.forEach((row) => {
      const key = patientKeyFromRow(row);
      const rowMedico = resolveMedicoFromRow(row, medicosById);
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: resolvePatientName(row),
          dni: normalizeDni(row.dni || row.paciente_dni),
          obraSocial: resolveObraSocialFromRow(row),
          medico: rowMedico.name,
          medicoId: rowMedico.id,
          treatments: []
        });
      }

      const patient = map.get(key);
      if (!patient.medico && rowMedico.name) {
        patient.medico = rowMedico.name;
        patient.medicoId = rowMedico.id;
      }
      if (!patient.obraSocial) patient.obraSocial = resolveObraSocialFromRow(row);
      const moduleMeta = MODULES[row._module];
      const rawTxId = `${row._module}-${row.id || row.pedido_id || patient.treatments.length + 1}`;
      const timeline = buildTreatmentTimeline(row, recetasByPedidoId);
      const override = txOverrides[rawTxId] || null;

      const conversationId = row?.conversation_id || row?.chatwoot_conversation_id || null;
      console.log('📋 row data for conversationId:', { row, conversationId }); // Debug log
      patient.treatments.push({
        id: rawTxId,
        pedidoId: row.id || row.pedido_id || null,
        conversationId,
        ingreso_origen: row?.ingreso_origen || null,
        medicamentoId: Number(row.medicamento_id) || null,
        medicoId: rowMedico.id,
        medico: rowMedico.name,
        moduleKey: moduleMeta.key,
        moduleTitle: moduleMeta.title,
        moduleRoute: moduleMeta.route,
        status: override?.status || statusLabel(row.estado),
        medication: override?.medication || getMedication(row, medById),
        medDetails: buildMedDetails(row, medById),
        diagnostico: override?.diagnostico || getDiagnostico(row),
        posologia: cleanField(row?.posologia),
        cantidadRecetas: resolveCantidadRecetas(row),
        periodoMeses: Math.max(Number(row?.periodo_meses) || 0, Number(row?.cantidad_recetas) || 0, 1),
        fechaInicio: normalizeFechaIso(row?.fecha_inicio || row?.fecha_solicitud),
        timeline
      });
    });

    Object.entries(manualTreatmentsByPatient).forEach(([patientKey, items]) => {
      const patient = map.get(patientKey);
      if (!patient || !Array.isArray(items)) return;
      items.forEach((tx) => {
        patient.treatments.push({
          ...tx,
          moduleTitle: tx.moduleTitle || 'Nuevo tratamiento',
          moduleRoute: tx.moduleRoute || '/pedidos-recetas-medico'
        });
      });
    });

    customPatients.forEach((custom) => {
      const existing = map.get(custom.key);
      if (existing) {
        existing.obraSocial = existing.obraSocial || custom.obraSocial || '';
        existing.medico = existing.medico || custom.medico || '';
        existing.treatments = [...existing.treatments, ...(custom.treatments || [])];
      } else {
        map.set(custom.key, { ...custom });
      }
    });

    const mergedPatients = Array.from(map.values());

    return mergedPatients
      .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
      .map((patient) => {
        const total = patient.treatments.length;
        const pendientes = patient.treatments.filter((tx) => isTxPendiente(tx.status)).length;
        const aprobados = patient.treatments.filter((tx) => isTxAprobado(tx.status)).length;
        const rechazados = patient.treatments.filter((tx) => isTxRechazado(tx.status)).length;
        const activos = pendientes + aprobados;
        const emitidas = patient.treatments.reduce(
          (acc, tx) => acc + tx.timeline.filter((r) => r.emitted).length,
          0
        );
        const totalRecetas = patient.treatments.reduce((acc, tx) => acc + tx.timeline.length, 0);
        const baseMedico = patient.medico || patient.treatments.find((tx) => normalizeText(tx.medico))?.medico || '';
        const baseMedicoId = patient.medicoId || patient.treatments.find((tx) => tx.medicoId)?.medicoId || null;
        const baseObraSocial = patient.obraSocial || '';
        const metaOverride = patientMetaOverrides[patient.key] || null;
        const medico = metaOverride && 'medico' in metaOverride ? metaOverride.medico : baseMedico;
        const medicoId = metaOverride && 'medicoId' in metaOverride ? metaOverride.medicoId : baseMedicoId;
        const obraSocial = metaOverride && 'obraSocial' in metaOverride ? metaOverride.obraSocial : baseObraSocial;
        const hasOmnicanal = patient.treatments.some((tx) => tx.ingreso_origen === 'n8n');
        return {
          ...patient,
          medico,
          medicoId,
          obraSocial,
          total,
          pendientes,
          aprobados,
          rechazados,
          activos,
          emitidas,
          totalRecetas,
          hasOmnicanal
        };
      });
  }, [rowsByModule, recetasByPedidoId, medById, txOverrides, manualTreatmentsByPatient, customPatients, medicosApi, patientMetaOverrides]);

  const patientMatchesStatus = (p, status) => {
    if (status === 'pending') return p.pendientes > 0;
    if (status === 'approved') return p.aprobados > 0;
    if (status === 'rejected') return p.rechazados > 0;
    return true;
  };

  const patientMatchesOmnicanal = (p, filter) => {
    if (filter === 'all') return true;
    if (filter === 'whatsapp') return p.hasOmnicanal === true;
    if (filter === 'manual') return p.hasOmnicanal === false;
    return true;
  };

  const filteredPatients = useMemo(() => {
    const q = normalizeText(search).toLowerCase();
    return patientDirectory.filter((p) => {
      if (!patientMatchesStatus(p, statusFilter)) return false;
      if (!patientMatchesOmnicanal(p, omnicanal)) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q)
        || String(p.dni).includes(q)
        || String(p.obraSocial || '').toLowerCase().includes(q)
      );
    });
  }, [patientDirectory, search, statusFilter, omnicanal]);

  const obraSocialOptions = useMemo(() => {
    const map = new Map();
    obrasSocialesApi.forEach((item) => {
      const value = normalizeText(item);
      if (value) map.set(value.toLowerCase(), value);
    });
    patientDirectory.forEach((p) => {
      const value = normalizeText(p.obraSocial);
      if (value) map.set(value.toLowerCase(), value);
    });
    return Array.from(map.values()).sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }));
  }, [obrasSocialesApi, patientDirectory]);

  const medicoPickerOptions = useMemo(() => {
    const map = new Map();
    medicosApi.forEach((m) => {
      const nombre = normalizeText(m.nombre);
      if (!nombre) return;
      map.set(nombre.toLowerCase(), {
        value: nombre,
        label: formatDoctorName(nombre),
        medicoId: Number(m.id) > 0 ? Number(m.id) : null
      });
    });
    patientDirectory.forEach((p) => {
      const nombre = normalizeText(p.medico);
      if (!nombre || map.has(nombre.toLowerCase())) return;
      map.set(nombre.toLowerCase(), {
        value: nombre,
        label: formatDoctorName(nombre),
        medicoId: null
      });
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }));
  }, [medicosApi, patientDirectory]);

  const resolveMedicoIdFromNombre = (nombre) => {
    const key = normalizeText(nombre).toLowerCase();
    if (!key) return null;
    const hit = medicoPickerOptions.find((o) => o.value.toLowerCase() === key);
    return hit?.medicoId ?? null;
  };

  const dniMatchFound = Boolean(dniMatch && dniMatch.found);

  const isDniStepValid = normalizeDni(newPatientForm.dni).length >= 6 && !dniMatchLoading;

  const isAltaValid = Boolean(
    normalizeText(newPatientForm.fullName).length >= 3
    && normalizeText(newPatientForm.birthDate)
    && normalizeText(newPatientForm.telefono).length >= 8
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeText(newPatientForm.email))
    && normalizeText(newPatientForm.obraSocial)
    && normalizeText(newPatientForm.nroAfiliado)
    && normalizeText(newPatientForm.medicoCabecera)
  );

  const isTratamientoValid = Boolean(
    normalizeText(newPatientForm.commercialName)
    && Number(newPatientForm.medicamentoId) > 0
    && (dniMatchFound ? normalizeText(newPatientForm.medicoCabecera) : true)
  );

  const selectedPatient = useMemo(
    () => filteredPatients.find((p) => p.key === selectedPatientKey) || null,
    [filteredPatients, selectedPatientKey]
  );

  const txStatusOf = (tx) => {
    if (isTxAprobado(tx.status)) return 'aprobado';
    if (isTxRechazado(tx.status)) return 'rechazado';
    return 'pendiente';
  };

  const txSourceOf = (tx) => (tx.ingreso_origen === 'n8n' ? 'whatsapp' : 'manual');

  const txNeedsEmit = (tx) => isTxAprobado(tx.status) && tx.timeline.some((rx) => !rx.emitted);

  const txIsFullyEmitted = (tx) => {
    const total = Array.isArray(tx?.timeline) ? tx.timeline.length : 0;
    if (total <= 0) return false;
    const emitted = tx.timeline.filter((rx) => rx?.emitted).length;
    return emitted >= total;
  };

  const txComparableDate = (tx) => normalizeFechaIso(
    tx.fechaInicio || tx.timeline.find((item) => normalizeFechaIso(item?.fechaPrevista))?.fechaPrevista || ''
  ) || '';

  const txStatusCounts = useMemo(() => {
    const list = selectedPatient ? selectedPatient.treatments : [];
    const acc = { all: list.length, pendiente: 0, aprobado: 0, rechazado: 0 };
    list.forEach((tx) => { acc[txStatusOf(tx)] += 1; });
    return acc;
  }, [selectedPatient]);

  const txSourceCounts = useMemo(() => {
    const list = selectedPatient ? selectedPatient.treatments : [];
    const acc = { all: list.length, whatsapp: 0, manual: 0, por_emitir: 0 };
    list.forEach((tx) => {
      acc[txSourceOf(tx)] += 1;
      if (txNeedsEmit(tx)) acc.por_emitir += 1;
    });
    return acc;
  }, [selectedPatient]);

  const txFiltered = useMemo(() => {
    const list = selectedPatient ? selectedPatient.treatments : [];
    const q = normalizeText(txSearch).trim().toLowerCase();
    const filtered = list.filter((tx) => {
      if (txStatusFilter !== 'all' && txStatusOf(tx) !== txStatusFilter) return false;
      if (txSourceFilter !== 'all' && txSourceOf(tx) !== txSourceFilter) return false;
      if (txFlowFilter === 'por_emitir' && !txNeedsEmit(tx)) return false;
      const txDate = txComparableDate(tx);
      if (txDateFrom && (!txDate || txDate < txDateFrom)) return false;
      if (txDateTo && (!txDate || txDate > txDateTo)) return false;
      if (!q) return true;
      const hay = [
        tx.medication,
        tx.medDetails?.generico,
        tx.medDetails?.presentacion,
        tx.medDetails?.laboratorio,
        tx.diagnostico,
        tx.moduleTitle,
        tx.status
      ]
        .filter(Boolean)
        .map((v) => normalizeText(String(v)).toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
    return filtered.slice().sort((a, b) => {
      const aFullyEmitted = txIsFullyEmitted(a);
      const bFullyEmitted = txIsFullyEmitted(b);
      if (aFullyEmitted !== bFullyEmitted) {
        return aFullyEmitted ? 1 : -1;
      }
      if (txSortMode === 'az') {
        return String(a.medication || '').localeCompare(String(b.medication || ''), 'es', { sensitivity: 'base' });
      }
      if (txSortMode === 'za') {
        return String(b.medication || '').localeCompare(String(a.medication || ''), 'es', { sensitivity: 'base' });
      }
      const dateA = txComparableDate(a);
      const dateB = txComparableDate(b);
      if (txSortMode === 'fecha_asc') {
        return String(dateA || '9999-12-31').localeCompare(String(dateB || '9999-12-31'));
      }
      return String(dateB || '').localeCompare(String(dateA || ''));
    });
  }, [selectedPatient, txSearch, txStatusFilter, txSourceFilter, txFlowFilter, txDateFrom, txDateTo, txSortMode]);
  const txTotal = txFiltered.length;
  const txTotalPages = txPageSize <= 0 ? 1 : Math.max(1, Math.ceil(txTotal / txPageSize));
  const txPageItems = useMemo(() => {
    if (txPageSize <= 0) return txFiltered;
    const start = (txPage - 1) * txPageSize;
    return txFiltered.slice(start, start + txPageSize);
  }, [txFiltered, txPage, txPageSize]);
  const txRangeStart = txTotal === 0 ? 0 : (txPageSize <= 0 ? 1 : (txPage - 1) * txPageSize + 1);
  const txRangeEnd = txPageSize <= 0 ? txTotal : Math.min(txPage * txPageSize, txTotal);
  const hasAdvancedTxFilter = txSourceFilter !== 'all'
    || txFlowFilter !== 'all'
    || txSortMode !== 'fecha_desc'
    || Boolean(txDateFrom)
    || Boolean(txDateTo);

  useEffect(() => {
    if (txPage > txTotalPages) setTxPage(1);
  }, [txPage, txTotalPages]);

  const cardDefaultSize = listCollapsed ? 10 : 8;
  const txSizeOptions = txViewMode === 'cards'
    ? (listCollapsed ? [10, 20, 40] : [8, 16, 32])
    : [5, 10, 25, 50];

  const changeTxViewMode = (mode) => {
    if (mode === txViewMode) return;
    setTxViewMode(mode);
    setTxPageSize(mode === 'cards' ? cardDefaultSize : 5);
    setTxPage(1);
  };

  useEffect(() => {
    if (txViewMode !== 'cards') return;
    setTxPageSize(listCollapsed ? 10 : 8);
    setTxPage(1);
  }, [listCollapsed, txViewMode]);

  const toggleInfoPop = useCallback((event, tx, kind) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const isPoso = kind === 'posologia';
    const content = normalizeText(isPoso ? tx?.posologia : tx?.diagnostico);
    setInfoPop((cur) => (cur && cur.txId === tx.id && cur.kind === kind
      ? null
      : {
        txId: tx.id,
        kind,
        medication: tx.medication,
        title: isPoso ? 'Posología' : 'Diagnóstico',
        icon: isPoso ? '💊' : '🩺',
        content,
        emptyText: isPoso
          ? 'Sin posología cargada para este tratamiento.'
          : 'Sin diagnóstico cargado para este tratamiento.',
        x: rect.right,
        y: rect.bottom + 10,
      }));
  }, []);

  useEffect(() => {
    if (!infoPop) return undefined;
    const onPointerDown = (e) => {
      if (e.target.closest('.crm-rr-poso-pop') || e.target.closest('.crm-rr-infobtn')) return;
      setInfoPop(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setInfoPop(null); };
    const onScroll = () => setInfoPop(null);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [infoPop]);

  useEffect(() => {
    if (selectedPatientKey && !filteredPatients.some((p) => p.key === selectedPatientKey)) {
      setSelectedPatientKey(null);
    }
  }, [filteredPatients, selectedPatientKey]);

  useEffect(() => {
    setNewTreatmentForm(NEW_TREATMENT_INITIAL);
    setIsNewTreatmentOpen(false);
    setMedSearchOpen(false);
    setMedSearchQuery('');
    setMedOptions([]);
    setEditingPatientMeta(false);
    setMetaPicker(null);
    setMetaQuery('');
    setTxPage(1);
    setTxSearch('');
    setTxStatusFilter('all');
  }, [selectedPatientKey]);

  useEffect(() => {
    if ((!isNewTreatmentOpen && !isNewPatientOpen) || !medSearchOpen) return undefined;
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      setMedSearchLoading(true);
      try {
        const q = String(medSearchQuery || '').trim();
        const qs = new URLSearchParams();
        qs.set('limit', q ? '80' : '40');
        if (q) qs.set('q', q);
        const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/medicamentos?${qs.toString()}`, {
          credentials: 'include',
          signal: ac.signal
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const meds = Array.isArray(json) ? json : (Array.isArray(json?.medicamentos) ? json.medicamentos : []);
        setMedOptions(meds);
      } catch (err) {
        if (err?.name !== 'AbortError') setMedOptions([]);
      } finally {
        if (!ac.signal.aborted) setMedSearchLoading(false);
      }
    }, 220);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [isNewTreatmentOpen, isNewPatientOpen, medSearchOpen, medSearchQuery]);

  useEffect(() => {
    if (!medSearchOpen) return undefined;
    const onDown = (e) => {
      const inModal = medSearchRef.current && medSearchRef.current.contains(e.target);
      const inDrawer = treatmentDrawerRef.current && treatmentDrawerRef.current.contains(e.target);
      if (!inModal && !inDrawer) {
        setMedSearchOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [medSearchOpen]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await fetchJson('/api/asociacion-recetas/medicos');
        if (!cancelled) setMedicosApi(normalizeMedicosFromApi(json));
      } catch (_) {
        if (!cancelled) setMedicosApi([]);
      } finally {
        if (!cancelled) setMedicosLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await fetchJson('/api/asociacion-recetas/obras-sociales-plex');
        const list = Array.isArray(json)
          ? json
          : (Array.isArray(json?.obras_sociales) ? json.obras_sociales : []);
        if (!cancelled) {
          setObrasSocialesApi(list.map((item) => normalizeText(item)).filter(Boolean));
        }
      } catch (_) {
        if (!cancelled) setObrasSocialesApi([]);
      } finally {
        if (!cancelled) setObrasSocialesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!isNewPatientOpen) return undefined;
    const dni = normalizeDni(newPatientForm.dni);
    if (dni.length < 6) {
      setDniMatch(null);
      setDniMatchLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    setDniMatchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/asociacion-recetas/clientes-por-dni/${encodeURIComponent(dni)}`,
          { credentials: 'include', signal: ac.signal }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (ac.signal.aborted) return;
        if (json?.found && json?.paciente) {
          const birthDate = normalizeFechaIso(json.fecha_nacimiento) || '';
          const match = {
            found: true,
            dni: normalizeDni(json.dni) || dni,
            name: normalizeText(json.paciente),
            birthDate,
            obraSocial: normalizeText(json.obra_social || ''),
            medico: normalizeText(json.prestador_geclisa || ''),
            source: json.source || 'geclisa'
          };
          setDniMatch(match);
          setNewPatientForm((prev) => ({
            ...prev,
            fullName: match.name || prev.fullName,
            birthDate: prev.birthDate || birthDate,
            obraSocial: prev.obraSocial || match.obraSocial,
            medicoCabecera: prev.medicoCabecera || match.medico
          }));
        } else {
          setDniMatch({ found: false, dni });
        }
      } catch (err) {
        if (err?.name !== 'AbortError') setDniMatch(null);
      } finally {
        if (!ac.signal.aborted) setDniMatchLoading(false);
      }
    }, 350);
    return () => {
      clearTimeout(timer);
      ac.abort();
    };
  }, [isNewPatientOpen, newPatientForm.dni]);

  const handleDraftChange = (field, value) => {
    setNewTreatmentForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'genericDrug' || field === 'commercialName' ? { medicamentoId: null } : null)
    }));
  };

  const handleCancelNewTreatment = () => {
    setNewTreatmentForm(NEW_TREATMENT_INITIAL);
    setIsNewTreatmentOpen(false);
    setEditingPedido(null);
    setMedSearchOpen(false);
    setMedSearchQuery('');
    setMedOptions([]);
  };

  const openNewTreatmentDrawer = () => {
    setEditingPedido(null);
    setNewTreatmentForm({ ...NEW_TREATMENT_INITIAL, startDate: todayIso() });
    setMedSearchOpen(false);
    setMedSearchQuery('');
    setMedOptions([]);
    setIsNewTreatmentOpen(true);
  };

  const openEditTreatment = (tx) => {
    if (!tx?.pedidoId) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'No se puede modificar',
        html: buildCrmSwalBody({ lead: 'Este tratamiento no tiene un pedido asociado en la base.' })
      });
      return;
    }
    setEditingPedido({ pedidoId: tx.pedidoId, txId: tx.id });
    setNewTreatmentForm({
      ...NEW_TREATMENT_INITIAL,
      genericDrug: normalizeText(tx.medDetails?.generico) || normalizeText(tx.medication),
      commercialName: normalizeText(tx.medDetails?.comercial),
      presentacion: normalizeText(tx.medDetails?.presentacion),
      medicamentoId: Number(tx.medicamentoId) || null,
      diagnosis: normalizeText(tx.diagnostico),
      posologia: normalizeText(tx.posologia) || '',
      startDate: normalizeFechaIso(tx.fechaInicio) || '',
      months: resolveMonthsSelectValue(tx.periodoMeses || tx.cantidadRecetas),
      destino: resolveDestinoFromModuleKey(tx.moduleKey)
    });
    setMedSearchOpen(false);
    setMedSearchQuery('');
    setMedOptions([]);
    setIsNewTreatmentOpen(true);
  };

  const handleSaveEditTreatment = async () => {
    if (!editingPedido?.pedidoId || savingTreatment) return;
    const medicamentoId = Number(newTreatmentForm.medicamentoId);
    if (!Number.isFinite(medicamentoId) || medicamentoId <= 0) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'Falta el medicamento',
        html: buildCrmSwalBody({
          lead: 'Seleccioná un medicamento de la <strong>lista de resultados</strong>.',
          footnote: 'Escribí la droga o el nombre comercial y elegí una opción del listado.'
        })
      });
      return;
    }

    setSavingTreatment(true);
    setProcessing({ title: 'Guardando cambios', sub: 'Actualizando el tratamiento en la base' });
    setIsNewTreatmentOpen(false);
    setMedSearchOpen(false);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${editingPedido.pedidoId}/medicamento`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          medicamento_id: medicamentoId,
          fecha_inicio: normalizeText(newTreatmentForm.startDate) || null,
          notas: normalizeText(newTreatmentForm.diagnosis) || null,
          posologia: normalizeText(newTreatmentForm.posologia) || null,
          cantidad_recetas: Math.max(1, Number(newTreatmentForm.months) || 1),
          periodo_meses: Math.max(1, Number(newTreatmentForm.months) || 1)
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const selectedKey = selectedPatient?.key || null;
      setNewTreatmentForm(NEW_TREATMENT_INITIAL);
      setEditingPedido(null);
      setMedSearchQuery('');
      setMedOptions([]);
      medByIdRef.current = {};
      setMedById({});
      await loadData({ silent: true });
      if (selectedKey) setSelectedPatientKey(selectedKey);
      void fireCrmAlert({
        icon: false,
        title: undefined,
        html: buildRrSuccessBody({
          title: 'Tratamiento modificado',
          message: 'El medicamento del tratamiento se actualizó en la base y los cambios ya se reflejan en el paciente.',
          wa: null
        }),
        width: 'min(400px, 92vw)',
        confirmButtonText: 'Listo',
        showCloseButton: true,
        allowOutsideClick: false
      });
    } catch (e) {
      setIsNewTreatmentOpen(true);
      void fireCrmAlert({
        icon: 'error',
        title: 'No se pudo modificar',
        html: buildCrmSwalBody({ lead: normalizeText(e?.message) || 'Ocurrió un error al modificar el tratamiento. Intentá nuevamente.' })
      });
    } finally {
      setSavingTreatment(false);
      setProcessing(null);
    }
  };

  const handleAddNewTreatment = async () => {
    if (!selectedPatient || savingTreatment) return;
    const dni = normalizeDni(selectedPatient.dni);
    const medicamentoId = Number(newTreatmentForm.medicamentoId);
    if (!dni || dni.length < 6) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'Paciente sin DNI',
        html: buildCrmSwalBody({ lead: 'El paciente seleccionado no tiene un <strong>DNI válido</strong>.' })
      });
      return;
    }
    if (!Number.isFinite(medicamentoId) || medicamentoId <= 0) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'Falta el medicamento',
        html: buildCrmSwalBody({
          lead: 'Seleccioná un medicamento de la <strong>lista de resultados</strong>.',
          footnote: 'Escribí la droga o el nombre comercial y elegí una opción del listado.'
        })
      });
      return;
    }

    setSavingTreatment(true);
    setProcessing({ title: 'Creando tratamiento', sub: 'Cargando el tratamiento en la base' });
    setIsNewTreatmentOpen(false);
    setMedSearchOpen(false);
    try {
      await createPedidoAltaRapida({
        dni,
        medicamentoId,
        destino: newTreatmentForm.destino,
        medicoId: resolveMedicoIdFromNombre(selectedPatient?.medico),
        medicoNombre: selectedPatient?.medico,
        months: newTreatmentForm.months,
        fechaInicio: newTreatmentForm.startDate,
        posologia: newTreatmentForm.posologia,
        notas: newTreatmentForm.diagnosis
      });
      setNewTreatmentForm(NEW_TREATMENT_INITIAL);
      setMedSearchQuery('');
      setMedOptions([]);
      await loadData({ silent: true });
      setSelectedPatientKey(`dni:${dni}`);
      void fireCrmAlert({
        icon: false,
        title: undefined,
        html: buildRrSuccessBody({
          title: 'Tratamiento creado',
          message: `Nuevo tratamiento para <strong>${normalizeText(selectedPatient.name) || `DNI ${formatDni(dni)}`}</strong>. Ya aparece en el paciente.`,
          wa: null
        }),
        width: 'min(400px, 92vw)',
        confirmButtonText: 'Listo',
        showCloseButton: true,
        allowOutsideClick: false
      });
    } catch (e) {
      setIsNewTreatmentOpen(true);
      void fireCrmAlert({
        icon: 'error',
        title: 'No se pudo crear el tratamiento',
        html: buildCrmSwalBody({ lead: normalizeText(e?.message) || 'Ocurrió un error al crear el tratamiento. Intentá nuevamente.' })
      });
    } finally {
      setSavingTreatment(false);
      setProcessing(null);
    }
  };

  const openNewPatientFlow = () => {
    setIsNewPatientOpen(true);
    setNewPatientStep('dni');
    setNewPatientForm({ ...NEW_PATIENT_INITIAL, startDate: todayIso() });
    setDniMatch(null);
    setDniMatchLoading(false);
    setAfilPicker(null);
    setAfilQuery('');
    setSavingAltaPaciente(false);
  };

  const closeNewPatientFlow = () => {
    setIsNewPatientOpen(false);
    setNewPatientStep('dni');
    setNewPatientForm(NEW_PATIENT_INITIAL);
    setMedSearchOpen(false);
    setDniMatch(null);
    setDniMatchLoading(false);
    setAfilPicker(null);
    setAfilQuery('');
    setSavingAltaPaciente(false);
  };

  const toggleAfilPicker = (which) => {
    setAfilPicker((prev) => (prev === which ? null : which));
    setAfilQuery('');
  };

  const selectAfilOption = (which, value, medicoId = null) => {
    if (which === 'obra') {
      setNewPatientForm((prev) => ({ ...prev, obraSocial: value }));
    } else {
      setNewPatientForm((prev) => ({
        ...prev,
        medicoCabecera: value,
        medicoCabeceraId: medicoId ?? resolveMedicoIdFromNombre(value)
      }));
    }
    setAfilPicker(null);
    setAfilQuery('');
  };

  const renderAfilDropdown = (kind, { floating = false } = {}) => {
    const opts = kind === 'obra'
      ? obraSocialOptions.map((o) => ({ value: o, label: o }))
      : medicoPickerOptions.map((o) => ({ value: o.value, label: o.label, medicoId: o.medicoId }));
    const q = afilQuery.trim().toLowerCase();
    const filtered = q ? opts.filter((o) => o.label.toLowerCase().includes(q)) : opts;
    const currentValue = kind === 'obra' ? newPatientForm.obraSocial : newPatientForm.medicoCabecera;
    const panelStyle = floating
      ? {
          ...styles.afilPanel,
          marginTop: 6,
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          height: 200,
          zIndex: 40,
          background: '#fff',
          boxShadow: '0 18px 40px rgba(15,39,68,.18), 0 2px 8px rgba(15,39,68,.08)'
        }
      : { ...styles.afilPanel, marginTop: 0, height: '100%' };
    return (
      <div style={panelStyle}>
        <div style={styles.afilPanelHead}>
          <input
            autoFocus
            className="crm-gd-input"
            style={styles.afilSearch}
            placeholder={kind === 'obra' ? 'Buscar obra social…' : 'Buscar médico…'}
            value={afilQuery}
            onChange={(e) => setAfilQuery(e.target.value)}
          />
          <span style={styles.afilCount}>{filtered.length}</span>
        </div>
        <div style={styles.afilList}>
          {filtered.length === 0 ? <div style={styles.afilEmpty}>Sin resultados</div> : null}
          {filtered.map((o) => (
            <button
              key={`afil-${kind}-${o.value}`}
              type="button"
              style={{ ...styles.afilItem, ...(o.value === currentValue ? styles.afilItemActive : null) }}
              onClick={() => selectAfilOption(kind, o.value, o.medicoId)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const openPatientMetaEdit = () => {
    if (!selectedPatient) return;
    setMetaDraft({
      medico: selectedPatient.medico || '',
      medicoId: selectedPatient.medicoId || resolveMedicoIdFromNombre(selectedPatient.medico),
      obraSocial: selectedPatient.obraSocial || ''
    });
    setMetaPicker(null);
    setMetaQuery('');
    setEditingPatientMeta(true);
  };

  const closePatientMetaEdit = () => {
    setEditingPatientMeta(false);
    setMetaPicker(null);
    setMetaQuery('');
  };

  const toggleMetaPicker = (which) => {
    setMetaPicker((prev) => (prev === which ? null : which));
    setMetaQuery('');
  };

  const selectMetaOption = (kind, value, medicoId = null) => {
    if (kind === 'obra') {
      setMetaDraft((prev) => ({ ...prev, obraSocial: value }));
    } else {
      setMetaDraft((prev) => ({
        ...prev,
        medico: value,
        medicoId: medicoId ?? resolveMedicoIdFromNombre(value)
      }));
    }
    setMetaPicker(null);
    setMetaQuery('');
  };

  const savePatientMeta = async () => {
    if (!selectedPatient || savingMeta) return;
    const key = selectedPatient.key;
    const dni = normalizeDni(selectedPatient.dni);
    if (!dni) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'Paciente sin DNI',
        html: buildCrmSwalBody({ lead: 'No se puede guardar porque el paciente no tiene un DNI válido.' })
      });
      return;
    }

    const nextMedico = normalizeText(metaDraft.medico);
    const nextMedicoId = metaDraft.medicoId || resolveMedicoIdFromNombre(metaDraft.medico);
    const nextObraSocial = normalizeText(metaDraft.obraSocial);
    const prevOverride = patientMetaOverrides[key];

    setPatientMetaOverrides((prev) => ({
      ...prev,
      [key]: { medico: nextMedico, medicoId: nextMedicoId, obraSocial: nextObraSocial }
    }));
    closePatientMetaEdit();

    setSavingMeta(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pacientes/${dni}/meta`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(Number(nextMedicoId) > 0 ? { medico_id: Number(nextMedicoId) } : {}),
          ...(nextMedico ? { medico: nextMedico } : {}),
          obra_social: nextObraSocial
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const json = await res.json().catch(() => ({}));
      const osTarget = json?.data?.obra_social_target;
      await loadData({ silent: true });
      setPatientMetaOverrides((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      if (nextObraSocial && osTarget == null) {
        void fireCrmAlert({
          icon: 'warning',
          title: 'Médico actualizado',
          html: buildCrmSwalBody({
            lead: 'El médico se guardó correctamente.',
            footnote: 'La obra social no se pudo persistir porque el paciente no tiene ficha de cliente registrada.'
          })
        });
      }
    } catch (e) {
      setPatientMetaOverrides((prev) => {
        const next = { ...prev };
        if (prevOverride) next[key] = prevOverride;
        else delete next[key];
        return next;
      });
      void fireCrmAlert({
        icon: 'error',
        title: 'No se pudieron guardar los cambios',
        html: buildCrmSwalBody({ lead: normalizeText(e?.message) || 'Ocurrió un error al actualizar el médico u obra social. Intentá nuevamente.' })
      });
    } finally {
      setSavingMeta(false);
    }
  };

  const renderMetaDropdown = (kind) => {
    const opts = kind === 'obra'
      ? obraSocialOptions.map((o) => ({ value: o, label: o }))
      : medicoPickerOptions.map((o) => ({ value: o.value, label: o.label, medicoId: o.medicoId }));
    const q = metaQuery.trim().toLowerCase();
    const filtered = q ? opts.filter((o) => o.label.toLowerCase().includes(q)) : opts;
    const currentValue = kind === 'obra' ? metaDraft.obraSocial : metaDraft.medico;
    return (
      <div
        className="crm-rr-meta-dropdown"
        style={{
          ...styles.afilPanel,
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 6,
          height: 214,
          zIndex: 60,
          background: '#fff',
          boxShadow: '0 18px 40px rgba(15,39,68,.18), 0 2px 8px rgba(15,39,68,.08)'
        }}
      >
        <div style={styles.afilPanelHead}>
          <input
            autoFocus
            className="crm-gd-input"
            style={styles.afilSearch}
            placeholder={kind === 'obra' ? 'Buscar obra social…' : 'Buscar médico…'}
            value={metaQuery}
            onChange={(e) => setMetaQuery(e.target.value)}
          />
          <span style={styles.afilCount}>{filtered.length}</span>
        </div>
        <div style={styles.afilList}>
          {filtered.length === 0 ? <div style={styles.afilEmpty}>Sin resultados</div> : null}
          {filtered.map((o) => (
            <button
              key={`meta-${kind}-${o.value}`}
              type="button"
              style={{ ...styles.afilItem, ...(o.value === currentValue ? styles.afilItemActive : null) }}
              onClick={() => selectMetaOption(kind, o.value, o.medicoId)}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const buildAltaRapidaBody = ({
    dni,
    medicamentoId,
    destino,
    medicoId,
    medicoNombre,
    months,
    fechaInicio,
    notas,
    posologia
  }) => {
    const cantidad = Math.max(1, Number(months) || 1);
    return {
      dni,
      medicamento_id: medicamentoId,
      es_pami: false,
      destino: destino === 'derivacion' ? 'derivacion' : 'aprobacion_medica',
      ...(Number(medicoId) > 0 ? { medico_id: Number(medicoId) } : {}),
      ...(normalizeText(medicoNombre) ? { medico: normalizeText(medicoNombre) } : {}),
      cantidad_recetas: cantidad,
      periodo_meses: cantidad,
      ...(normalizeText(fechaInicio) ? { fecha_inicio: normalizeText(fechaInicio) } : {}),
      ...(normalizeText(notas) ? { notas: normalizeText(notas) } : {}),
      ...(normalizeText(posologia) ? { posologia: normalizeText(posologia) } : {})
    };
  };

  const createPedidoAltaRapida = async (payload) => {
    const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/alta-rapida`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildAltaRapidaBody(payload))
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || `HTTP ${res.status}`);
    }
    return res.json();
  };

  const saveNewPatient = async () => {
    if (savingPatient) return;
    const dni = normalizeDni(newPatientForm.dni);
    const medicamentoId = Number(newPatientForm.medicamentoId);
    if (!dni || dni.length < 6) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'DNI inválido',
        html: buildCrmSwalBody({ lead: 'Ingresá un DNI válido de <strong>al menos 6 dígitos</strong> para continuar.' })
      });
      return;
    }
    if (!Number.isFinite(medicamentoId) || medicamentoId <= 0) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'Falta el medicamento',
        html: buildCrmSwalBody({
          lead: 'Seleccioná un medicamento de la <strong>lista de resultados</strong>.',
          footnote: 'Escribí la droga o el nombre comercial y elegí una opción del listado.'
        })
      });
      return;
    }

    setSavingPatient(true);
    try {
      await createPedidoAltaRapida({
        dni,
        medicamentoId,
        destino: newPatientForm.destino,
        medicoId: Number(newPatientForm.medicoCabeceraId) || resolveMedicoIdFromNombre(newPatientForm.medicoCabecera),
        medicoNombre: newPatientForm.medicoCabecera,
        months: newPatientForm.months,
        fechaInicio: newPatientForm.startDate,
        notas: newPatientForm.diagnosis,
        posologia: newPatientForm.posologia
      });
      await loadData({ silent: true });
      setSelectedPatientKey(`dni:${dni}`);
      closeNewPatientFlow();
      void fireCrmAlert({
        icon: false,
        title: 'Pedido creado',
        html: buildCrmSwalBody({
          lead: `El tratamiento para <strong>DNI ${formatDni(dni)}</strong> se cargó en la base.`,
          footnote: 'Ya aparece en el directorio con su estado inicial.'
        }),
        confirmButtonText: 'Listo'
      });
    } catch (e) {
      void fireCrmAlert({
        icon: 'error',
        title: 'No se pudo crear el pedido',
        html: buildCrmSwalBody({ lead: normalizeText(e?.message) || 'Ocurrió un error al crear el pedido. Intentá nuevamente.' })
      });
    } finally {
      setSavingPatient(false);
    }
  };

  const goNextFromDni = () => {
    if (!isDniStepValid) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'DNI incompleto',
        html: buildCrmSwalBody({ lead: 'Ingresá un DNI válido de <strong>al menos 6 dígitos</strong> y esperá la búsqueda.' })
      });
      return;
    }
    setAfilPicker(null);
    setNewPatientStep(dniMatchFound ? 'tratamiento' : 'alta');
  };

  const goBackNewPatient = () => {
    setAfilPicker(null);
    setMedSearchOpen(false);
    if (newPatientStep === 'tratamiento') {
      setNewPatientStep(dniMatchFound ? 'dni' : 'alta');
      return;
    }
    if (newPatientStep === 'alta') {
      setNewPatientStep('dni');
    }
  };

  const registerAltaPaciente = async () => {
    if (savingAltaPaciente) return;
    if (!isAltaValid) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'Datos del alta incompletos',
        html: buildCrmSwalBody({ lead: 'Completá nombre y apellido, fecha de nacimiento, teléfono, email, obra social y número de afiliado.' })
      });
      return;
    }
    const dni = normalizeDni(newPatientForm.dni);
    setSavingAltaPaciente(true);
    try {
      await crearClienteExternoAltaRapida({
        nombre_completo: normalizeText(newPatientForm.fullName),
        dni,
        fecha_nacimiento: normalizeText(newPatientForm.birthDate) || null,
        telefono: normalizeText(newPatientForm.telefono) || null,
        email: normalizeText(newPatientForm.email) || null,
        obra_social: normalizeText(newPatientForm.obraSocial) || null,
        nro_afiliado: normalizeText(newPatientForm.nroAfiliado) || null
      }, API_BASE_URL);
      setAfilPicker(null);
      setNewPatientStep('tratamiento');
    } catch (e) {
      if (e?.code === 'DUPLICATE_DNI') {
        setAfilPicker(null);
        setNewPatientStep('tratamiento');
        return;
      }
      void fireCrmAlert({
        icon: 'error',
        title: 'No se pudo registrar el paciente',
        html: buildCrmSwalBody({ lead: normalizeText(e?.message) || 'Ocurrió un error al dar de alta al paciente. Intentá nuevamente.' })
      });
    } finally {
      setSavingAltaPaciente(false);
    }
  };

  const openMedSearch = (scope, field, value) => {
    setMedSearchField(`${scope}:${field}`);
    setMedSearchQuery(String(value || '').trim());
    setMedSearchOpen(true);
  };

  const applyMedicationOption = (med) => {
    const medId = Number(med?.id);
    if (medSearchField.startsWith('newPatient:')) {
      setNewPatientForm((prev) => ({
        ...prev,
        genericDrug: normalizeText(med?.nombre_generico || prev.genericDrug),
        commercialName: normalizeText(med?.nombre_comercial || prev.commercialName),
        presentacion: normalizeText(med?.presentacion || prev.presentacion),
        medicamentoId: Number.isFinite(medId) && medId > 0 ? medId : prev.medicamentoId
      }));
    } else {
      setNewTreatmentForm((prev) => ({
        ...prev,
        genericDrug: normalizeText(med?.nombre_generico || prev.genericDrug),
        commercialName: normalizeText(med?.nombre_comercial || prev.commercialName),
        presentacion: normalizeText(med?.presentacion || prev.presentacion),
        medicamentoId: Number.isFinite(medId) && medId > 0 ? medId : prev.medicamentoId
      }));
    }
    setMedSearchOpen(false);
  };

  const confirmFinalizedTreatmentAction = async (tx, intent) => {
    const status = normalizeText(tx.status) || 'Procesado';
    const intentLabel = intent === 'approve' ? 'aprobar' : 'rechazar';
    const result = await fireConfirmDialog({
      icon: 'warning',
      title: 'Tratamiento ya procesado',
      html: buildCrmSwalBody({
        lead: `Este tratamiento ya está <strong>${status}</strong>.`,
        footnote: `No podés ${intentLabel}lo nuevamente desde acá. ¿Deseás hacer una modificación?`
      }),
      confirmButtonText: 'Sí, modificar',
      cancelButtonText: 'Cancelar',
      width: 'min(420px, 92vw)'
    });
    if (result.isConfirmed) openEditTreatment(tx);
  };

  const handleApproveClick = (tx) => {
    console.log('📋 Opening approve modal with tx:', tx); // Debug log
    if (isTxAprobado(tx.status)) {
      void confirmFinalizedTreatmentAction(tx, 'approve');
      return;
    }
    if (!isTxAprobable(tx.status)) {
      void fireCrmAlert({
        icon: 'warning',
        title: 'No se puede aprobar',
        html: buildCrmSwalBody({
          lead: 'Solo se puede aprobar un pedido pendiente médico o rechazado.'
        })
      });
      return;
    }
    setApproveModal(tx);
    setApproveWa(false);
    setApproveStep('whatsapp');
  };

  const closeApproveModal = () => {
    if (pedidoActionId) return;
    setApproveModal(null);
    setApproveWa(false);
    setApproveStep('whatsapp');
  };

  const handleRejectClick = async (tx) => {
    if (isTxRechazado(tx.status)) {
      void confirmFinalizedTreatmentAction(tx, 'reject');
      return;
    }
    if (isTxAprobado(tx.status)) {
      const result = await fireConfirmDialog({
        icon: false,
        title: undefined,
        width: 'min(420px, 92vw)',
        html: buildRrConfirmBody({
          title: 'Rechazar tratamiento aprobado',
          message: `Vas a rechazar el tratamiento de ${normalizeText(tx.medication) || 'este medicamento'}, que ya estaba aprobado.`,
          items: [
            'Se corta el plan de prescripción y no se podrán emitir más recetas',
            'Usalo si el paciente abandona, suspende o ya no continúa',
            'Queda registrado en el historial del pedido'
          ]
        }),
        confirmButtonText: 'Sí, rechazar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc2626'
      });
      if (!result.isConfirmed) return;
      void runRejectFlow(tx);
      return;
    }
    void runRejectFlow(tx);
  };

  const showRxBlockedNotice = (txId, rxId) => {
    if (rxNoticeTimerRef.current) clearTimeout(rxNoticeTimerRef.current);
    setRxBlockedNotice({ txId, rxId });
    rxNoticeTimerRef.current = setTimeout(() => {
      setRxBlockedNotice((cur) => (
        cur?.txId === txId && cur?.rxId === rxId ? null : cur
      ));
    }, 3200);
  };

  const emitirUrlValid = useMemo(() => {
    const url = normalizeText(emitirUrl);
    if (!url) return true;
    return isValidHttpUrl(url);
  }, [emitirUrl]);
  const canSubmitEmision = useMemo(
    () => !confirmandoEmision && Boolean(normalizeText(emitirNro)) && emitirUrlValid,
    [confirmandoEmision, emitirNro, emitirUrlValid]
  );

  const closeEmitModal = () => {
    if (confirmandoEmision) return;
    setEmitModal(null);
    setEmitirNro('');
    setEmitirUrl('');
    setEmitWa(false);
    setEmitStep('whatsapp');
  };

  const openEmitModal = (tx, rx) => {
    if (rx.emitted) return;
    if (isTxRechazado(tx.status) || isTxPendiente(tx.status)) {
      showRxBlockedNotice(tx.id, rx.id);
      return;
    }
    if (!isTxAprobado(tx.status)) return;
    const secuencialBlock = resolveSecuencialEmisionBlock(tx.timeline, rx);
    if (secuencialBlock) {
      void fireCrmAlert({
        icon: 'warning',
        title: secuencialBlock,
        timer: 3800,
        showConfirmButton: false
      });
      return;
    }
    setEmitModal({ tx, rx });
    setEmitirNro(normalizeText(rx.nroExterno) || `REC-EXT-${String(rx.numero).padStart(2, '0')}`);
    setEmitirUrl(normalizeText(rx.recetaUrl));
    setEmitWa(false);
    setEmitStep('whatsapp');
  };

  const confirmarEmisionReceta = async () => {
    if (!emitModal?.rx?.id || !canSubmitEmision) return;
    const { tx, rx } = emitModal;
    const selectedKey = selectedPatient?.key || null;
    const nroExterno = normalizeText(emitirNro);
    const recetaUrl = normalizeText(emitirUrl) || null;
    const waEmitir = emitWa;

    setConfirmandoEmision(true);
    setProcessing({
      title: 'Registrando emisión',
      sub: waEmitir ? 'Emitiendo la receta y enviando WhatsApp' : 'Emitiendo la receta en el sistema externo'
    });
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/recetas/${rx.id}/emitir`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nro_externo: nroExterno,
          receta_url: recetaUrl,
          notificar_whatsapp_emitir: waEmitir ? 1 : 0,
          disparar_webhook_emitir_receta: waEmitir ? 1 : 0
        })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const json = await res.json().catch(() => ({}));
      const emitWh = json?.webhook || {};
      const waState = waEmitir ? (emitWh.enviado ? 'sent' : 'failed') : null;
      setEmitirNro('');
      setEmitirUrl('');
      setEmitWa(false);
      setEmitStep('whatsapp');
      setEmitModal(null);
      await loadData({ silent: true });
      if (selectedKey) setSelectedPatientKey(selectedKey);
      void fireCrmAlert({
        icon: false,
        title: undefined,
        html: buildRrSuccessBody({
          title: 'Receta registrada',
          message: `${rx.label} quedó marcada como emitida en el sistema externo.`,
          wa: waState
        }),
        width: 'min(400px, 92vw)',
        confirmButtonText: 'Listo',
        showCloseButton: true,
        allowOutsideClick: false
      });
    } catch (e) {
      setEmitModal({ tx, rx });
      setEmitirNro(nroExterno);
      setEmitirUrl(recetaUrl || '');
      setEmitWa(waEmitir);
      void fireCrmAlert({
        icon: 'error',
        title: 'No se pudo registrar',
        html: buildCrmSwalBody({ lead: normalizeText(e?.message) || 'Intentá nuevamente.' })
      });
    } finally {
      setConfirmandoEmision(false);
      setProcessing(null);
    }
  };

  const handleRxPillClick = (tx, rx) => {
    openEmitModal(tx, rx);
  };

  const runApproveFlow = async (tx) => {
    if (!tx?.pedidoId || pedidoActionId) return;
    const selectedKey = selectedPatient?.key || null;
    setPedidoActionId(tx.id);
    setProcessing({
      title: 'Aprobando tratamiento',
      sub: approveWa ? 'Generando el plan y enviando WhatsApp' : 'Generando el plan de prescripción'
    });
    try {
      const cantidad = resolveCantidadRecetas({
        cantidad_recetas: tx.cantidadRecetas,
        periodo_meses: tx.periodoMeses
      });
      const medicoId = Number(tx.medicoId);
      const waAlta = approveWa;
      const body = {
        cantidad_recetas: cantidad,
        periodo_meses: cantidad,
        disparar_webhook_alta_tratamiento: waAlta ? 1 : 0
      };
      if (tx.fechaInicio) body.fecha_inicio = tx.fechaInicio;
      if (Number.isFinite(medicoId) && medicoId > 0) body.medico_id = medicoId;
      if (normalizeText(tx.medico)) body.medico = normalizeText(tx.medico);
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${tx.pedidoId}/aprobar`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const json = await res.json().catch(() => ({}));
      const altaWh = json?.webhook?.alta_tratamiento || json?.data?.webhook?.alta_tratamiento || {};
      const waState = waAlta ? (altaWh.enviado ? 'sent' : 'failed') : null;
      setApproveModal(null);
      setApproveWa(false);
      setApproveStep('whatsapp');
      await loadData({ silent: true });
      if (selectedKey) setSelectedPatientKey(selectedKey);
      void fireCrmAlert({
        icon: false,
        title: undefined,
        html: buildRrSuccessBody({
          title: 'Tratamiento aprobado',
          message: 'El plan de prescripción quedó generado y listo para emitir.',
          wa: waState
        }),
        width: 'min(400px, 92vw)',
        confirmButtonText: 'Listo',
        showCloseButton: true,
        allowOutsideClick: false
      });
    } catch (e) {
      void fireCrmAlert({
        icon: 'error',
        title: 'No se pudo aprobar',
        html: buildCrmSwalBody({ lead: normalizeText(e?.message) || 'Intentá nuevamente.' })
      });
    } finally {
      setPedidoActionId(null);
      setProcessing(null);
    }
  };

  const runRejectFlow = async (tx) => {
    if (!tx?.pedidoId || pedidoActionId) return;
    const selectedKey = selectedPatient?.key || null;
    setPedidoActionId(tx.id);
    setProcessing({ title: 'Rechazando tratamiento', sub: 'Actualizando el estado del pedido' });
    try {
      const res = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos/${tx.pedidoId}/rechazar`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: 'Rechazado desde el router de recetas' })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      await loadData({ silent: true });
      if (selectedKey) setSelectedPatientKey(selectedKey);
      void fireCrmAlert({
        icon: false,
        title: undefined,
        html: buildRrSuccessBody({
          title: 'Tratamiento rechazado',
          message: 'El tratamiento quedó rechazado y el estado se actualizó en la base.',
          tone: 'danger'
        }),
        width: 'min(400px, 92vw)',
        confirmButtonText: 'Listo',
        showCloseButton: true,
        allowOutsideClick: false
      });
    } catch (e) {
      void fireCrmAlert({
        icon: 'error',
        title: 'No se pudo rechazar',
        html: buildCrmSwalBody({ lead: normalizeText(e?.message) || 'Intentá nuevamente.' })
      });
    } finally {
      setPedidoActionId(null);
      setProcessing(null);
    }
  };

  if (!activeModules.length) {
    return (
      <section className="crm-rr-page">
        <div className="crm-rr-empty">
          <div className="crm-rr-empty__icon">🔒</div>
          <div className="crm-rr-empty__title">Sin permisos</div>
          <div className="crm-rr-empty__text">
            Necesitás el permiso <strong>ver_directorio</strong> o al menos uno de: derivación, pedidos médico o recetas a emitir.
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="crm-rr-page">
      {infoPop ? (
        <div
          className={`crm-rr-poso-pop crm-rr-poso-pop--${infoPop.kind}`}
          style={{ left: `${infoPop.x}px`, top: `${infoPop.y}px` }}
          role="dialog"
          aria-label={infoPop.title}
        >
          <span className="crm-rr-poso-pop__arrow" aria-hidden="true" />
          <div className="crm-rr-poso-pop__head">
            <span className="crm-rr-poso-pop__ic" aria-hidden="true">{infoPop.icon}</span>
            <span className="crm-rr-poso-pop__title">{infoPop.title}</span>
            {infoPop.medication ? (
              <span className="crm-rr-poso-pop__med" title={infoPop.medication}>{infoPop.medication}</span>
            ) : null}
          </div>
          <div className={`crm-rr-poso-pop__body${infoPop.content ? '' : ' is-empty'}`}>
            {infoPop.content || infoPop.emptyText}
          </div>
        </div>
      ) : null}
      {processing ? (
        <div className="crm-rr-loading-overlay crm-rr-loading-overlay--fixed" role="status" aria-live="assertive">
          <div className="crm-rr-loading-card">
            <div className="crm-rr-loading-spinner" aria-hidden="true">
              <span className="crm-rr-loading-spinner__dot" />
            </div>
            <div className="crm-rr-loading-copy">
              <div className="crm-rr-loading-title">
                {processing.title}
                <span className="crm-rr-loading-dots" />
              </div>
              <div className="crm-rr-loading-sub">{processing.sub}</div>
            </div>
          </div>
        </div>
      ) : null}
      {loading ? (
        <div className="crm-rr-loading" role="status" aria-live="polite">
          <div className="crm-rr-loading__card">
            <div className="crm-rr-loading__spinner" aria-hidden="true" />
            <div className="crm-rr-loading__title">Preparando directorio de recetas</div>
            <div className="crm-rr-loading__sub">Cargando pedidos y tratamientos…</div>
          </div>
        </div>
      ) : null}

      <header className="crm-rr-head">
        <div className="crm-rr-head__main">
          <div className="crm-rr-head__icon" aria-hidden="true">🔗</div>
          <div style={{ display: 'flex' }}>
            <h3 className="crm-rr-head__title">Directorio</h3>
          </div>
        </div>
        <div className="crm-rr-head__actions">
          <div className="crm-omnicanal-switch">
            <button
              type="button"
              className={`crm-omnicanal-switch__btn ${omnicanal === 'all' ? 'is-active' : ''}`}
              onClick={() => setOmnicanal('all')}
              title="Mostrar todos los pacientes"
            >
              Todos
            </button>
            <button
              type="button"
              className={`crm-omnicanal-switch__btn ${omnicanal === 'whatsapp' ? 'is-active' : ''}`}
              onClick={() => setOmnicanal('whatsapp')}
              title="Mostrar solo pacientes de WhatsApp"
            >
              WhatsApp
            </button>
            <button
              type="button"
              className={`crm-omnicanal-switch__btn ${omnicanal === 'manual' ? 'is-active' : ''}`}
              onClick={() => setOmnicanal('manual')}
              title="Mostrar solo pacientes de carga manual"
            >
              Manual
            </button>
          </div>
          <button type="button" className="crm-rr-btn-new" onClick={openNewPatientFlow}>
            <span className="crm-rr-btn-new__plus" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="11" height="11">
                <path d="M12 5v14M5 12h14" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" />
              </svg>
            </span>
            <span className="crm-rr-btn-new__label">Nuevo paciente</span>
          </button>
        </div>
      </header>

      {error ? <div className="crm-gd-empty">{error}</div> : null}

      <div className={`crm-rr-grid${listCollapsed ? ' is-list-collapsed' : ''}`}>
          <aside className={`crm-rr-panel crm-rr-list-panel${listCollapsed ? ' is-collapsed' : ''}`}>
          {listCollapsed ? (
            <div className="crm-rr-rail">
              <button
                type="button"
                className="crm-rr-rail__toggle"
                onClick={() => setListCollapsed(false)}
                title="Expandir lista de pacientes"
                aria-label="Expandir lista de pacientes"
              >
                <span className="crm-rr-rail__toggle-icon" aria-hidden="true">»</span>
                <span className="crm-rr-rail__toggle-count">{filteredPatients.length}</span>
              </button>
              <div className="crm-rr-rail__dock">
                {filteredPatients.map((patient) => {
                  const isActive = selectedPatient?.key === patient.key;
                  return (
                    <button
                      key={`rail-${patient.key}`}
                      type="button"
                      className={`crm-rr-rail__avatar${isActive ? ' is-active' : ''}`}
                      title={`${patient.name} · DNI ${formatDni(patient.dni)}`}
                      aria-label={patient.name}
                      onClick={() => setSelectedPatientKey(patient.key)}
                    >
                      <span className="crm-rr-rail__avatar-ini" aria-hidden="true">
                        {String(patient.name || '?').charAt(0).toUpperCase()}
                      </span>
                      {patient.activos > 0 ? (
                        <span
                          className="crm-rr-rail__avatar-badge"
                          title={`${patient.activos} ${patient.activos === 1 ? 'tratamiento activo' : 'tratamientos activos'}`}
                        >
                          {patient.activos}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <>
            <div className="crm-rr-panel-head">
              <span className="crm-rr-panel-head__title">Pacientes</span>
              <span className="crm-rr-panel-head__meta">{filteredPatients.length} resultados</span>
            </div>
            <div className="crm-rr-search-row">
              <div className="crm-rr-search-wrap">
                <span className="crm-rr-search-icon" aria-hidden="true">🔍</span>
                <input
                  className="crm-rr-search-input"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar por paciente, dni u obra social…"
                />
              </div>
              <button
                type="button"
                className="crm-rr-collapse-btn"
                onClick={() => setListCollapsed(true)}
                title="Colapsar lista de pacientes"
                aria-label="Colapsar lista de pacientes"
              >
                <span className="crm-rr-collapse-btn__icon" aria-hidden="true">«</span>
              </button>
            </div>
            <div className="crm-rr-statusbar" role="group" aria-label="Filtrar por estado">
              {[
                { key: 'all', label: 'Todos', tone: 'all' },
                { key: 'pending', label: 'Pendientes', tone: 'pending' },
                { key: 'approved', label: 'Aprobados', tone: 'approved' },
                { key: 'rejected', label: 'Rechazados', tone: 'rejected' }
              ].map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  className={`crm-rr-statuschip crm-rr-statuschip--${chip.tone}${statusFilter === chip.key ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(chip.key)}
                  aria-pressed={statusFilter === chip.key}
                  title={`Filtrar: ${chip.label}`}
                >
                  <span className="crm-rr-statuschip__dot" aria-hidden="true" />
                  <span className="crm-rr-statuschip__label">{chip.label}</span>
                </button>
              ))}
            </div>
            <div className="crm-rr-list">
              {filteredPatients.length === 0 && !loading ? (
                (() => {
                  const hasFilters = Boolean(normalizeText(search)) || statusFilter !== 'all' || omnicanal !== 'all';
                  return (
                    <div className="crm-rr-list-empty">
                      <span className="crm-rr-list-empty__badge" aria-hidden="true">
                        <span className="crm-rr-list-empty__ring" />
                        <svg viewBox="0 0 24 24" width="22" height="22" fill="none">
                          <circle cx="10.5" cy="10.5" r="6.2" stroke="currentColor" strokeWidth="2" />
                          <path d="M15.4 15.4 20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                      </span>
                      <span className="crm-rr-list-empty__title">
                        {hasFilters ? 'Sin coincidencias' : 'Aún no hay pacientes'}
                      </span>
                      <span className="crm-rr-list-empty__text">
                        {hasFilters
                          ? 'Probá ajustar la búsqueda o cambiar el filtro de estado.'
                          : 'Cuando cargues pedidos van a aparecer en este directorio.'}
                      </span>
                      {hasFilters ? (
                        <button
                          type="button"
                          className="crm-rr-list-empty__btn"
                          onClick={() => { setSearch(''); setStatusFilter('all'); setOmnicanal('all'); }}
                        >
                          Limpiar filtros
                        </button>
                      ) : null}
                    </div>
                  );
                })()
              ) : null}
              {filteredPatients.map((patient) => {
                const isActive = selectedPatient?.key === patient.key;
                return (
                  <button
                    key={patient.key}
                    type="button"
                    onClick={() => setSelectedPatientKey(patient.key)}
                    className={`crm-rr-row${isActive ? ' is-selected' : ''}`}
                  >
                    <span className="crm-rr-row__avatar" aria-hidden="true">
                      {String(patient.name || '?').charAt(0).toUpperCase()}
                    </span>
                    <span className="crm-rr-row__body">
                      <span className="crm-rr-row__name">{patient.name}</span>
                      <span className="crm-rr-row__meta">
                        DNI {formatDni(patient.dni)}{patient.obraSocial ? ` · ${patient.obraSocial}` : ''}
                      </span>
                    </span>
                    <span className="crm-rr-row__counts">
                      {patient.pendientes > 0 ? (
                        <span className="crm-rr-count crm-rr-count--pending" title={`${patient.pendientes} pendientes`}>
                          {patient.pendientes}
                        </span>
                      ) : null}
                      {patient.aprobados > 0 ? (
                        <span className="crm-rr-count crm-rr-count--approved" title={`${patient.aprobados} aprobados`}>
                          {patient.aprobados}
                        </span>
                      ) : null}
                      {patient.rechazados > 0 ? (
                        <span className="crm-rr-count crm-rr-count--rejected" title={`${patient.rechazados} rechazados`}>
                          {patient.rechazados}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
            </>
          )}
          </aside>

          <main className="crm-rr-panel crm-rr-detail">
            {!selectedPatient ? (
              <div className="crm-rr-detail-empty">
                <span className="crm-rr-detail-empty__badge" aria-hidden="true">
                  <span className="crm-rr-detail-empty__ring" />
                  <svg viewBox="0 0 24 24" width="30" height="30" fill="none">
                    <circle cx="12" cy="8" r="3.6" stroke="currentColor" strokeWidth="2" />
                    <path d="M5 19.2c0-3.2 3.1-5.2 7-5.2s7 2 7 5.2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </span>
                <span className="crm-rr-detail-empty__title">Seleccioná un paciente</span>
                <span className="crm-rr-detail-empty__text">Elegí a alguien del directorio para ver su información y tratamientos</span>
              </div>
            ) : (
              <>
                <div className="crm-rr-detail-head">
                  <button
                    type="button"
                    className="crm-rr-back"
                    title={listCollapsed ? 'Mostrar listado de pacientes' : 'Volver al listado'}
                    aria-label="Volver al listado"
                    onClick={() => {
                      if (listCollapsed) setListCollapsed(false);
                      else setSelectedPatientKey(null);
                    }}
                  >
                    ←
                  </button>
                  <div className="crm-rr-detail-avatar">{String(selectedPatient.name || '?').charAt(0).toUpperCase()}</div>
                  <div className="crm-rr-detail-main">
                    <div className="crm-rr-detail-top">
                      {editingPatientMeta ? (
                        <div className="crm-rr-meta-edit">
                          <div className="crm-rr-meta-edit__head">
                            <span className="crm-rr-meta-edit__name" title={selectedPatient.name}>{selectedPatient.name}</span>
                            <span className="crm-rr-meta-edit__dni">DNI {formatDni(selectedPatient.dni)}</span>
                          </div>
                          <div className="crm-rr-meta-edit__fields">
                            <div className="crm-rr-meta-field">
                              <span className="crm-rr-meta-field__label">Médico tratante</span>
                              <div className="crm-rr-meta-field__control">
                                <button
                                  type="button"
                                  className={`crm-rr-meta-trigger${metaPicker === 'medico' ? ' is-open' : ''}`}
                                  onClick={() => toggleMetaPicker('medico')}
                                >
                                  <span className="crm-rr-meta-trigger__val">
                                    {metaDraft.medico ? formatDoctorName(metaDraft.medico) : 'Seleccionar médico'}
                                  </span>
                                  <span className="crm-rr-meta-trigger__caret" aria-hidden="true">▾</span>
                                </button>
                                {metaPicker === 'medico' ? renderMetaDropdown('medico') : null}
                              </div>
                            </div>
                            <div className="crm-rr-meta-field">
                              <span className="crm-rr-meta-field__label">Obra social</span>
                              <div className="crm-rr-meta-field__control">
                                <button
                                  type="button"
                                  className={`crm-rr-meta-trigger${metaPicker === 'obra' ? ' is-open' : ''}`}
                                  onClick={() => toggleMetaPicker('obra')}
                                >
                                  <span className="crm-rr-meta-trigger__val">
                                    {metaDraft.obraSocial || 'Seleccionar obra social'}
                                  </span>
                                  <span className="crm-rr-meta-trigger__caret" aria-hidden="true">▾</span>
                                </button>
                                {metaPicker === 'obra' ? renderMetaDropdown('obra') : null}
                              </div>
                            </div>
                          </div>
                          <div className="crm-rr-meta-edit__actions">
                            <button type="button" className="crm-rr-meta-btn crm-rr-meta-btn--ghost" onClick={closePatientMetaEdit}>
                              Cancelar
                            </button>
                            <button type="button" className="crm-rr-meta-btn crm-rr-meta-btn--save" onClick={savePatientMeta}>
                              Guardar cambios
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="crm-rr-detail-info"
                          onClick={openPatientMetaEdit}
                          title="Editar médico y obra social"
                        >
                          <span className="crm-rr-detail-info__pencil" aria-hidden="true">
                            <svg viewBox="0 0 16 16" width="12" height="12">
                              <path d="M11.6 1.6a1.4 1.4 0 0 1 2 2l-.9.9-2-2 .9-.9zM9.9 3.3l2 2L5.4 11.8l-2.6.6.6-2.6 6.5-6.5z" fill="currentColor" />
                            </svg>
                          </span>
                          <span className="crm-rr-detail-identity">
                            <span className="crm-rr-detail-name" title={selectedPatient.name}>
                              {selectedPatient.name}
                            </span>
                            {normalizeText(selectedPatient.medico) ? (
                              <>
                                <span className="crm-rr-detail-sep" aria-hidden="true">·</span>
                                <span className="crm-rr-detail-medico" title="Médico tratante">
                                  {formatDoctorName(selectedPatient.medico)}
                                </span>
                              </>
                            ) : null}
                          </span>
                          <span className="crm-rr-detail-sub">
                            <span className="crm-rr-detail-meta">
                              DNI {formatDni(selectedPatient.dni)}
                            </span>
                            {normalizeText(selectedPatient.obraSocial) ? (
                              <>
                                <span className="crm-rr-detail-sep" aria-hidden="true">·</span>
                                <span className="crm-rr-detail-os" title={`Obra social: ${selectedPatient.obraSocial}`}>
                                  <span className="crm-rr-detail-os__k">Obra social</span>
                                  <span className="crm-rr-detail-os__v">{selectedPatient.obraSocial}</span>
                                </span>
                              </>
                            ) : null}
                          </span>
                        </button>
                      )}
                      <div className="crm-rr-detail-actions">
                        <div className="crm-rr-detail-actions__row">
                          <div className="crm-rr-tx-curation">
                            <div className="crm-rr-tx-curation__sources" role="group" aria-label="Filtrar tratamientos por origen">
                              {[
                                { key: 'all', label: 'Todo', count: txSourceCounts.all },
                                { key: 'whatsapp', label: 'WhatsApp', count: txSourceCounts.whatsapp },
                                { key: 'manual', label: 'Manual', count: txSourceCounts.manual }
                              ].map((source) => (
                                <button
                                  key={source.key}
                                  type="button"
                                  className={`crm-rr-tx-curation__chip${txSourceFilter === source.key ? ' is-active' : ''}`}
                                  onClick={() => { setTxSourceFilter(source.key); setTxPage(1); }}
                                  aria-pressed={txSourceFilter === source.key}
                                  title={source.label}
                                >
                                  <span className="crm-rr-tx-curation__chip-label">{source.label}</span>
                                  <span className="crm-rr-tx-curation__chip-count">{source.count}</span>
                                </button>
                              ))}
                            </div>
                            {/* Boton "Por emitir" oculto del header por pedido de UI; se conserva la logica del filtro. */}
                            <div className="crm-rr-tx-curation__field">
                              <span className="crm-rr-tx-curation__lbl">Orden</span>
                              <div className="crm-rr-tx-select-wrap">
                                <select
                                  className="crm-rr-tx-select crm-rr-tx-select--curation"
                                  value={txSortMode}
                                  onChange={(e) => { setTxSortMode(e.target.value); setTxPage(1); }}
                                  aria-label="Ordenar tratamientos"
                                >
                                  <option value="fecha_desc">Fecha: reciente</option>
                                  <option value="fecha_asc">Fecha: antigua</option>
                                  <option value="az">A a Z</option>
                                  <option value="za">Z a A</option>
                                </select>
                                <span className="crm-rr-tx-select__caret" aria-hidden="true">▾</span>
                              </div>
                            </div>
                          </div>
                          <div className="crm-rr-tx-toggle" role="group" aria-label="Modo de vista de tratamientos">
                            <button
                              type="button"
                              className={`crm-rr-tx-toggle__btn${txViewMode === 'rows' ? ' is-active' : ''}`}
                              onClick={() => changeTxViewMode('rows')}
                              title="Ver en filas"
                              aria-pressed={txViewMode === 'rows'}
                            >
                              <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                                <rect x="1.5" y="2.5" width="13" height="3.2" rx="1.1" fill="currentColor" />
                                <rect x="1.5" y="10.3" width="13" height="3.2" rx="1.1" fill="currentColor" />
                              </svg>
                              Filas
                            </button>
                            <button
                              type="button"
                              className={`crm-rr-tx-toggle__btn${txViewMode === 'cards' ? ' is-active' : ''}`}
                              onClick={() => changeTxViewMode('cards')}
                              title="Ver en tarjetas"
                              aria-pressed={txViewMode === 'cards'}
                            >
                              <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                                <rect x="1.5" y="1.5" width="5.4" height="5.4" rx="1.1" fill="currentColor" />
                                <rect x="9.1" y="1.5" width="5.4" height="5.4" rx="1.1" fill="currentColor" />
                                <rect x="1.5" y="9.1" width="5.4" height="5.4" rx="1.1" fill="currentColor" />
                                <rect x="9.1" y="9.1" width="5.4" height="5.4" rx="1.1" fill="currentColor" />
                              </svg>
                              Tarjetas
                            </button>
                          </div>
                          <button
                            type="button"
                            className="crm-rr-add-tx"
                            onClick={openNewTreatmentDrawer}
                          >
                            <span className="crm-rr-add-tx__plus" aria-hidden="true">+</span>
                            <span className="crm-rr-add-tx__label">Agregar otro tratamiento</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {(selectedPatient && selectedPatient.treatments.length > 0) ? (
                  <div className="crm-rr-tx-toolbar">
                    <div className="crm-rr-tx-filterbar">
                      {[
                        { key: 'all', label: 'Todos', tone: 'all' },
                        { key: 'pendiente', label: 'Pendientes', tone: 'pendiente' },
                        { key: 'aprobado', label: 'Aprobados', tone: 'aprobado' },
                        { key: 'rechazado', label: 'Rechazados', tone: 'rechazado' }
                      ].map((f) => (
                        <button
                          key={f.key}
                          type="button"
                          className={`crm-rr-txchip crm-rr-txchip--${f.tone}${txStatusFilter === f.key ? ' is-active' : ''}`}
                          onClick={() => { setTxStatusFilter(f.key); setTxPage(1); }}
                          aria-pressed={txStatusFilter === f.key}
                          title={f.label}
                        >
                          <span className="crm-rr-txchip__dot" aria-hidden="true" />
                          <span className="crm-rr-txchip__label">{f.label}</span>
                          <span className="crm-rr-txchip__count">{txStatusCounts[f.key] ?? 0}</span>
                        </button>
                      ))}
                    </div>
                    <div className="crm-rr-tx-search">
                      <span className="crm-rr-tx-search__ic" aria-hidden="true">🔎</span>
                      <input
                        type="text"
                        className="crm-rr-tx-search__input"
                        placeholder="Buscar medicamento, droga o diagnóstico…"
                        value={txSearch}
                        onChange={(e) => { setTxSearch(e.target.value); setTxPage(1); }}
                        aria-label="Buscar tratamientos"
                      />
                      {txSearch ? (
                        <button
                          type="button"
                          className="crm-rr-tx-search__clear"
                          onClick={() => { setTxSearch(''); setTxPage(1); }}
                          aria-label="Limpiar búsqueda"
                        >
                          ✕
                        </button>
                      ) : null}
                    </div>
                    <div className="crm-rr-tx-toolbar__size">
                      <span className="crm-rr-tx-toolbar__lbl">Mostrar</span>
                      <div className="crm-rr-tx-select-wrap">
                        <select
                          className="crm-rr-tx-select"
                          value={txPageSize <= 0 ? 'ALL' : String(txPageSize)}
                          onChange={(e) => {
                            const v = e.target.value;
                            setTxPageSize(v === 'ALL' ? 0 : Number(v));
                            setTxPage(1);
                          }}
                          aria-label="Tratamientos por página"
                        >
                          {txSizeOptions.map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                          <option value="ALL">Todos</option>
                        </select>
                        <span className="crm-rr-tx-select__caret" aria-hidden="true">▾</span>
                      </div>
                    </div>
                    <span className="crm-rr-tx-toolbar__count">{txTotal} trat.</span>
                  </div>
                ) : null}
                {(txTotal === 0 && (normalizeText(txSearch) || txStatusFilter !== 'all' || hasAdvancedTxFilter) && selectedPatient && selectedPatient.treatments.length > 0) ? (
                  <div className="crm-rr-tx-noresults">
                    <span className="crm-rr-tx-noresults__ic" aria-hidden="true">🔍</span>
                    <div className="crm-rr-tx-noresults__title">Sin coincidencias</div>
                    <div className="crm-rr-tx-noresults__text">
                      {normalizeText(txSearch)
                        ? <>No hay tratamientos que coincidan con “{txSearch}”{txStatusFilter !== 'all' ? ' en este estado' : ''}.</>
                        : 'No hay tratamientos para la curación actual.'}
                    </div>
                    <button type="button" className="crm-rr-tx-noresults__btn" onClick={() => { setTxSearch(''); setTxStatusFilter('all'); setTxSourceFilter('all'); setTxFlowFilter('all'); setTxSortMode('fecha_desc'); setTxDateFrom(''); setTxDateTo(''); setTxPage(1); }}>
                      Limpiar filtros
                    </button>
                  </div>
                ) : null}
                <div className={`crm-rr-tx-list${txViewMode === 'cards' ? ' crm-rr-tx-list--cards' : ''}`}>
                {txPageItems.map((tx, txIdx) => {
                  const txApproved = isTxAprobado(tx.status);
                  const txApprovable = isTxAprobable(tx.status);
                  const isWhatsappTx = tx.ingreso_origen === 'n8n';
                  const emittedCount = tx.timeline.filter((r) => r.emitted).length;
                  const totalRx = tx.timeline.length;
                  const txFullyEmitted = totalRx > 0 && emittedCount >= totalRx;
                  const txInteractionLocked = pedidoActionId === tx.id || txApproved || txFullyEmitted;
                  const actionsLocked = txInteractionLocked;
                  const rejectLocked = pedidoActionId === tx.id || isTxRechazado(tx.status) || txFullyEmitted;
                  const emittedPct = totalRx ? Math.round((emittedCount / totalRx) * 100) : 0;
                  const medLine = [tx.medDetails?.generico, tx.medDetails?.presentacion, tx.medDetails?.laboratorio]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <article
                      key={tx.id}
                      className={`crm-rr-tx-card crm-rr-tx-card--c${txIdx % 2}${txViewMode === 'cards' && isWhatsappTx ? ' crm-rr-tx-card--has-origin' : ''}${txFullyEmitted ? ' crm-rr-tx-card--fully-emitted' : ''}`}
                    >
                      {txFullyEmitted ? (
                        <div className="crm-rr-tx-card__done-ribbon" title={`Plan completo: ${emittedCount}/${totalRx} emitidas`}>
                          <span className="crm-rr-tx-card__done-ribbon-ic" aria-hidden="true">✦</span>
                          <span>100% emitido</span>
                        </div>
                      ) : null}
                      {txViewMode === 'cards' ? (
                        <>
                          <header className="crm-rr-tx-card__head crm-rr-tx-card__head--card">
                            <span className="crm-rr-tx-card__icon" aria-hidden="true">💊</span>
                            <span className="crm-rr-tx-card__name" title={tx.medication}>{tx.medication}</span>
                            <span
                              className={`crm-rr-tx-card__statusdot crm-rr-tx-card__statusdot--${statusBadge(tx.status).tone}`}
                              title={tx.status}
                              aria-label={`Estado: ${tx.status}`}
                            >
                              <span className="crm-rr-tx-card__statusdot-ic" aria-hidden="true">{statusBadge(tx.status).icon}</span>
                            </span>
                          </header>
                          {isWhatsappTx ? (
                            <div className="crm-rr-tx-card__origin-row">
                              <span className="crm-rr-tx-card__origin-badge" title="Ingreso por WhatsApp">
                                WhatsApp
                              </span>
                            </div>
                          ) : null}
                          {(tx.medDetails?.generico || tx.medDetails?.presentacion || tx.medDetails?.laboratorio) ? (
                            <div className="crm-rr-tx-card__sub">
                              <div className="crm-rr-tx-card__spec">
                                {tx.medDetails?.generico ? (
                                  <span className="crm-rr-tx-card__drug" title={tx.medDetails.generico}>{tx.medDetails.generico}</span>
                                ) : null}
                                {tx.medDetails?.presentacion ? (
                                  <span className="crm-rr-tx-card__pres" title={tx.medDetails.presentacion}>{tx.medDetails.presentacion}</span>
                                ) : null}
                                {tx.medDetails?.laboratorio ? (
                                  <span className="crm-rr-tx-card__lab" title={`Laboratorio: ${tx.medDetails.laboratorio}`}>{tx.medDetails.laboratorio}</span>
                                ) : null}
                              </div>
                            </div>
                          ) : null}
                        </>
                      ) : (
                        <header className="crm-rr-tx-card__head">
                          <span className="crm-rr-tx-card__icon" aria-hidden="true">💊</span>
                          <div className="crm-rr-tx-card__titles">
                            <div className="crm-rr-tx-card__titlerow">
                              <span className="crm-rr-tx-card__name" title={tx.medication}>{tx.medication}</span>
                              {isWhatsappTx ? (
                                <span className="crm-rr-tx-card__origin-badge" title="Ingreso por WhatsApp">
                                  WhatsApp
                                </span>
                              ) : null}
                              {medLine ? (
                                <span className="crm-rr-tx-card__detail" title={medLine}>{medLine}</span>
                              ) : null}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`crm-rr-infobtn crm-rr-infobtn--poso${infoPop && infoPop.txId === tx.id && infoPop.kind === 'posologia' ? ' is-active' : ''}`}
                            onClick={(e) => toggleInfoPop(e, tx, 'posologia')}
                            disabled={txFullyEmitted}
                            title={txFullyEmitted ? 'Plan 100% emitido' : 'Ver posología'}
                            aria-label="Ver posología"
                          >
                            {RR_EYE_SVG}
                          </button>
                          <button
                            type="button"
                            className={`crm-rr-infobtn crm-rr-infobtn--dx${infoPop && infoPop.txId === tx.id && infoPop.kind === 'diagnostico' ? ' is-active' : ''}`}
                            onClick={(e) => toggleInfoPop(e, tx, 'diagnostico')}
                            disabled={txFullyEmitted}
                            title={txFullyEmitted ? 'Plan 100% emitido' : 'Ver diagnóstico'}
                            aria-label="Ver diagnóstico"
                          >
                            {RR_DX_SVG}
                          </button>
                          <span className={`${statusPillClass(tx.status)} crm-rr-tx-card__status`}>{tx.status}</span>
                        </header>
                      )}

                      <div className="crm-rr-tx-actions">
                        {txViewMode === 'cards' ? (
                          <div className="crm-rr-tx-actions__info">
                            <button
                              type="button"
                              className={`crm-rr-infobtn crm-rr-infobtn--poso${infoPop && infoPop.txId === tx.id && infoPop.kind === 'posologia' ? ' is-active' : ''}`}
                              onClick={(e) => toggleInfoPop(e, tx, 'posologia')}
                              disabled={txFullyEmitted}
                              title={txFullyEmitted ? 'Plan 100% emitido' : 'Ver posología'}
                              aria-label="Ver posología"
                            >
                              {RR_EYE_SVG}
                            </button>
                            <button
                              type="button"
                              className={`crm-rr-infobtn crm-rr-infobtn--dx${infoPop && infoPop.txId === tx.id && infoPop.kind === 'diagnostico' ? ' is-active' : ''}`}
                              onClick={(e) => toggleInfoPop(e, tx, 'diagnostico')}
                              disabled={txFullyEmitted}
                              title={txFullyEmitted ? 'Plan 100% emitido' : 'Ver diagnóstico'}
                              aria-label="Ver diagnóstico"
                            >
                              {RR_DX_SVG}
                            </button>
                          </div>
                        ) : null}
                        <button
                          type="button"
                          className={`crm-rr-act crm-rr-act--approve${txViewMode === 'cards' ? ' crm-rr-act--icon' : ''}`}
                          onClick={() => handleApproveClick(tx)}
                          disabled={actionsLocked || !txApprovable}
                          title={txViewMode === 'cards'
                            ? (txFullyEmitted
                              ? 'Plan 100% emitido'
                              : (txApproved
                              ? 'Tratamiento ya aprobado'
                              : (!txApprovable
                                ? 'Solo se puede aprobar un pedido pendiente médico o rechazado'
                                : (pedidoActionId === tx.id ? 'Aprobando…' : 'Aprobar'))))
                            : (txFullyEmitted
                              ? 'Plan 100% emitido'
                              : (txApproved
                              ? 'Tratamiento ya aprobado'
                              : (!txApprovable ? 'Solo se puede aprobar un pedido pendiente médico o rechazado' : undefined)))}
                          aria-label="Aprobar"
                        >
                          {txViewMode === 'cards'
                            ? RR_ACT_CHECK
                            : (pedidoActionId === tx.id ? 'Aprobando…' : (txApproved ? 'Aprobado' : 'Aprobar'))}
                        </button>
                        <button
                          type="button"
                          className={`crm-rr-act crm-rr-act--reject${txViewMode === 'cards' ? ' crm-rr-act--icon' : ''}`}
                          onClick={() => handleRejectClick(tx)}
                          disabled={rejectLocked}
                          title={txViewMode === 'cards'
                            ? (txFullyEmitted
                              ? 'Plan 100% emitido'
                              : (txApproved ? 'Rechazar (abortar) el tratamiento aprobado' : (isTxRechazado(tx.status) ? 'El tratamiento ya está rechazado' : (pedidoActionId === tx.id ? 'Rechazando…' : 'Rechazar'))))
                            : (txFullyEmitted
                              ? 'Plan 100% emitido'
                              : (txApproved ? 'Rechazar (abortar) el tratamiento aprobado' : (isTxRechazado(tx.status) ? 'El tratamiento ya está rechazado' : undefined)))}
                          aria-label="Rechazar"
                        >
                          {txViewMode === 'cards'
                            ? RR_ACT_X
                            : (pedidoActionId === tx.id ? 'Rechazando…' : 'Rechazar')}
                        </button>
                        <button
                          type="button"
                          className={`crm-rr-act crm-rr-act--edit crm-rr-act--right${txViewMode === 'cards' ? ' crm-rr-act--icon' : ''}`}
                          onClick={() => openEditTreatment(tx)}
                          disabled={actionsLocked}
                          title={txViewMode === 'cards'
                            ? (txFullyEmitted ? 'Plan 100% emitido' : (txApproved ? 'El tratamiento está aprobado' : 'Modificar'))
                            : (txFullyEmitted ? 'Plan 100% emitido' : (txApproved ? 'El tratamiento está aprobado' : undefined))}
                          aria-label="Modificar"
                        >
                          {txViewMode === 'cards' ? RR_ACT_PENCIL : 'Modificar'}
                        </button>
                      </div>

                      {tx.timeline.length > 0 ? (
                        <div className="crm-rr-rx-track">
                          <div className="crm-rr-rx-head">
                            <span className="crm-rr-rx-head__label">Plan de prescripción</span>
                            <span className="crm-rr-rx-head__sep" aria-hidden="true">—</span>
                            {txApproved ? (
                              <span className="crm-rr-rx-head__count">
                                <strong>{emittedCount}</strong>/{totalRx} emitidas
                              </span>
                            ) : (
                              <span className="crm-rr-rx-head__warn">Requiere aprobación para emitir</span>
                            )}
                          </div>
                          <div className="crm-rr-rx-track__body">
                            <div className="crm-rr-rx-progress" aria-hidden="true">
                              <span className="crm-rr-rx-progress__fill" style={{ width: `${emittedPct}%` }} />
                            </div>
                            <div className="crm-rr-rx-pills">
                              {tx.timeline.map((rx) => {
                                const secuencialBlock = resolveSecuencialEmisionBlock(tx.timeline, rx);
                                const blockedApproval = isTxRechazado(tx.status) || isTxPendiente(tx.status);
                                const canEmit = isTxAprobado(tx.status)
                                  && !rx.emitted
                                  && !blockedApproval
                                  && !secuencialBlock;
                                const pillTitle = blockedApproval
                                  ? 'Requiere aprobación para emitir'
                                  : rx.emitted
                                    ? `Emitida${rx.nroExterno ? ` · ${rx.nroExterno}` : ''}${rx.fechaPrevista ? ` · ${formatFechaCorta(rx.fechaPrevista)}` : ''}`
                                    : secuencialBlock
                                      ? secuencialBlock
                                      : `Registrar emisión${rx.fechaPrevista ? ` · ${formatFechaCorta(rx.fechaPrevista)}` : ''}`;
                                return (
                                  <button
                                    key={rx.id}
                                    type="button"
                                    title={pillTitle}
                                    onClick={() => handleRxPillClick(tx, rx)}
                                    disabled={rx.emitted}
                                    className={[
                                      'crm-rr-rx',
                                      rx.emitted ? ' is-done' : '',
                                      blockedApproval ? ' is-blocked' : '',
                                      !rx.emitted && !blockedApproval && secuencialBlock ? ' is-locked' : '',
                                      canEmit ? ' is-ready' : '',
                                      rxBlockedNotice?.txId === tx.id && rxBlockedNotice?.rxId === rx.id ? ' is-notice' : ''
                                    ].join('')}
                                  >
                                    <span className="crm-rr-rx__num">{rx.label}</span>
                                    {rx.monthLabel ? (
                                      <span className="crm-rr-rx__mes">{rx.monthLabel}</span>
                                    ) : null}
                                  </button>
                                );
                              })}
                              {rxBlockedNotice?.txId === tx.id ? (
                                <span className="crm-rr-rx-notice">Requiere aprobación para emitir</span>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </article>
                  );
                })}
                </div>
                {txTotal > 0 ? (
                  <div className="crm-rr-pager">
                    <div className="crm-rr-pager__meta">
                      Mostrando <strong>{txRangeStart}</strong>–<strong>{txRangeEnd}</strong> de <strong>{txTotal}</strong>
                    </div>
                    {txPageSize > 0 ? (
                      <nav className="crm-rr-pager__nav" aria-label="Paginación de tratamientos">
                        <button
                          type="button"
                          className="crm-rr-pager__btn"
                          onClick={() => setTxPage(1)}
                          disabled={txPage <= 1}
                          aria-label="Primera página"
                        >
                          «
                        </button>
                        <button
                          type="button"
                          className="crm-rr-pager__btn"
                          onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                          disabled={txPage <= 1}
                          aria-label="Página anterior"
                        >
                          ‹
                        </button>
                        <span className="crm-rr-pager__current">
                          Página <strong>{txPage}</strong> de {txTotalPages}
                        </span>
                        <button
                          type="button"
                          className="crm-rr-pager__btn"
                          onClick={() => setTxPage((p) => Math.min(txTotalPages, p + 1))}
                          disabled={txPage >= txTotalPages}
                          aria-label="Página siguiente"
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          className="crm-rr-pager__btn"
                          onClick={() => setTxPage(txTotalPages)}
                          disabled={txPage >= txTotalPages}
                          aria-label="Última página"
                        >
                          »
                        </button>
                      </nav>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}
          </main>
      </div>

      <div
        style={{
          ...styles.drawerBackdrop,
          ...(isNewTreatmentOpen ? styles.drawerBackdropOpen : styles.drawerBackdropClosed)
        }}
        onClick={(e) => { if (e.target === e.currentTarget) handleCancelNewTreatment(); }}
      >
        <aside
          style={{
            ...styles.drawerPanel,
            ...(isNewTreatmentOpen ? styles.drawerPanelOpen : styles.drawerPanelClosed)
          }}
          ref={treatmentDrawerRef}
        >
          <div style={styles.drawerHeader}>
            <div>
              <div style={styles.drawerTitle}>{editingPedido ? 'Modificar tratamiento' : 'Nuevo tratamiento'}</div>
              <div style={styles.drawerSub}>
                {selectedPatient ? `Para ${selectedPatient.name}` : 'Para este paciente'}
              </div>
            </div>
            <button type="button" className="crm-gd-btn crm-gd-btn--ghost" onClick={handleCancelNewTreatment}>
              Cerrar
            </button>
          </div>

          <div style={styles.drawerBody}>
            <div style={styles.drawerGrid}>
              <div>
                <label className="crm-gd-field-label">Nombre comercial</label>
                <input
                  className="crm-gd-input"
                  placeholder="Ej: Lipitor"
                  value={newTreatmentForm.commercialName}
                  onFocus={() => openMedSearch('newTreatment', 'commercialName', newTreatmentForm.commercialName)}
                  onChange={(e) => {
                    handleDraftChange('commercialName', e.target.value);
                    openMedSearch('newTreatment', 'commercialName', e.target.value);
                  }}
                />
              </div>
              <div>
                <label className="crm-gd-field-label">Presentación</label>
                <input
                  className="crm-gd-input"
                  placeholder="Se completa al elegir el medicamento"
                  value={newTreatmentForm.presentacion}
                  readOnly
                />
              </div>
              <div style={styles.newTxSpan2}>
                <div style={styles.afilPanel}>
                  {medSearchOpen && (medSearchField === 'newTreatment:genericDrug' || medSearchField === 'newTreatment:commercialName') ? (
                    <>
                      <div style={styles.afilPanelHead}>
                        <span style={styles.medPanelTitle}>
                          <span style={styles.medPanelIcon}>💊</span>
                          Resultados por nombre comercial
                        </span>
                        <span style={styles.afilCount}>{medSearchLoading ? '…' : medOptions.length}</span>
                      </div>
                      <div style={styles.afilList}>
                        {medSearchLoading ? (
                          <div style={styles.medPanelState}>
                            <span className="crm-gd-spinner" style={styles.miniSpinner} />
                            Buscando medicamentos…
                          </div>
                        ) : null}
                        {!medSearchLoading && medOptions.length === 0 ? (
                          <div style={styles.afilEmpty}>Sin resultados</div>
                        ) : null}
                        {!medSearchLoading && medOptions.slice(0, 12).map((med) => {
                          const parts = splitMedicamentoOption(med);
                          return (
                            <button
                              key={`nt-med-${med.id}`}
                              type="button"
                              className="crm-rr-med-opt"
                              style={styles.medOptItem}
                              onClick={() => applyMedicationOption(med)}
                            >
                              <span style={styles.medOptPrimary}>{parts.primary}</span>
                              {parts.secondary ? <span style={styles.medOptSecondary}>{parts.secondary}</span> : null}
                            </button>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div style={styles.afilHintWrap}>
                      <span style={styles.afilHintIcon}>🔎</span>
                      <span style={styles.afilHint}>Escribí el nombre comercial para ver las opciones acá</span>
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.newTxSpan2}>
                <label className="crm-gd-field-label">Diagnostico</label>
                <input
                  className="crm-gd-input"
                  placeholder="Diagnostico"
                  value={newTreatmentForm.diagnosis}
                  onChange={(e) => handleDraftChange('diagnosis', e.target.value)}
                />
              </div>
              <div style={styles.newTxSpan2}>
                <label className="crm-gd-field-label">Posología</label>
                <input
                  className="crm-gd-input"
                  placeholder="Ej: 1 comprimido cada 8 horas"
                  value={newTreatmentForm.posologia}
                  onChange={(e) => handleDraftChange('posologia', e.target.value)}
                />
              </div>
              <div>
                <label className="crm-gd-field-label">Fecha de inicio</label>
                <input
                  type="date"
                  className="crm-gd-input"
                  value={newTreatmentForm.startDate}
                  onChange={(e) => handleDraftChange('startDate', e.target.value)}
                />
              </div>
              <div>
                <label className="crm-gd-field-label">Cantidad de meses</label>
                <select
                  className="crm-gd-select"
                  value={newTreatmentForm.months}
                  onChange={(e) => handleDraftChange('months', e.target.value)}
                >
                  <option value="1">1 mes</option>
                  <option value="3">3 meses</option>
                  <option value="6">6 meses</option>
                  <option value="12">12 meses</option>
                </select>
              </div>
              <div style={styles.newTxSpan2}>
                <label className="crm-gd-field-label">Destino del pedido</label>
                <select
                  className="crm-gd-select"
                  value={newTreatmentForm.destino}
                  onChange={(e) => handleDraftChange('destino', e.target.value)}
                >
                  <option value="aprobacion_medica">Pendiente aprobación médica</option>
                  <option value="derivacion">Pendiente derivación</option>
                </select>
              </div>
            </div>
          </div>

          <div style={styles.drawerFooter}>
            <button type="button" className="crm-gd-btn crm-gd-btn--ghost" onClick={handleCancelNewTreatment} disabled={savingTreatment}>
              Cancelar
            </button>
            <button
              type="button"
              className="crm-gd-btn crm-gd-btn--primary"
              onClick={editingPedido ? handleSaveEditTreatment : handleAddNewTreatment}
              disabled={savingTreatment || !(Number(newTreatmentForm.medicamentoId) > 0)}
              style={(savingTreatment || !(Number(newTreatmentForm.medicamentoId) > 0)) ? styles.btnDisabled : undefined}
            >
              {savingTreatment
                ? (editingPedido ? 'Guardando…' : 'Creando…')
                : (editingPedido ? 'Guardar cambios' : 'Agregar tratamiento')}
            </button>
          </div>
        </aside>
      </div>

      {isNewPatientOpen ? (
        <div style={styles.newPatientBackdrop}>
          <div style={styles.newPatientModal} ref={medSearchRef}>
            <div style={styles.newPatientHeader}>
              <div>
                <div style={styles.newPatientTitle}>Nuevo paciente</div>
                <div style={styles.newPatientSub}>Alta al programa de prescripción recurrente</div>
              </div>
              <button type="button" className="crm-gd-btn crm-gd-btn--ghost" onClick={closeNewPatientFlow}>Cerrar</button>
            </div>

            <div style={styles.newPatientBody}>
            {(() => {
              const altaIncluded = newPatientStep === 'alta' || Boolean(dniMatch && dniMatch.found === false);
              const steps = [{ key: 'dni', label: 'Paciente' }];
              if (altaIncluded) steps.push({ key: 'alta', label: 'Alta' });
              steps.push({ key: 'tratamiento', label: 'Tratamiento' });
              const currentIdx = steps.findIndex((s) => s.key === newPatientStep);
              return (
                <div style={styles.newPatientSteps}>
                  {steps.map((s, i) => {
                    const done = currentIdx > i;
                    const active = s.key === newPatientStep;
                    return (
                      <span
                        key={s.key}
                        style={{ ...styles.stepPill, ...(active ? styles.stepPillActive : (done ? styles.stepPillDone : null)) }}
                      >
                        {done ? '✓ ' : `${i + 1}. `}{s.label}
                      </span>
                    );
                  })}
                </div>
              );
            })()}

            {newPatientStep === 'dni' ? (
              <div style={styles.newPatientGrid}>
                <div style={{ ...styles.medSearchCell, ...styles.newTxSpan2 }}>
                  <label className="crm-gd-field-label">DNI del paciente</label>
                  <input
                    className="crm-gd-input"
                    inputMode="numeric"
                    placeholder="Ej: 12345678"
                    autoFocus
                    value={newPatientForm.dni}
                    onChange={(e) => setNewPatientForm((prev) => ({ ...prev, dni: e.target.value }))}
                  />
                  {normalizeDni(newPatientForm.dni).length >= 6 ? (
                    <div className="crm-rr-dni-match-wrap">
                      {dniMatchLoading ? (
                        <div className="crm-rr-dni-match crm-rr-dni-match--loading">
                          <span className="crm-gd-spinner" style={styles.miniSpinner} />
                          <span className="crm-rr-dni-match__searching">Buscando coincidencias por DNI…</span>
                        </div>
                      ) : dniMatch && dniMatch.found ? (
                        <div className="crm-rr-dni-match crm-rr-dni-match--found">
                          <div className="crm-rr-dni-match__avatar" aria-hidden>
                            {(dniMatch.name || '?').trim().charAt(0).toUpperCase()}
                          </div>
                          <div className="crm-rr-dni-match__info">
                            <div className="crm-rr-dni-match__name">{dniMatch.name}</div>
                            <div className="crm-rr-dni-match__meta">
                              DNI {formatDni(dniMatch.dni)}
                              {dniMatch.birthDate ? ` · ${formatFechaCorta(dniMatch.birthDate)}` : ''}
                              {dniMatch.obraSocial ? ` · ${dniMatch.obraSocial}` : ''}
                            </div>
                          </div>
                          <span className="crm-rr-dni-match__badge">
                            <span className="crm-rr-dni-match__check">✓</span>
                            Datos cargados
                          </span>
                        </div>
                      ) : dniMatch && dniMatch.found === false ? (
                        <>
                          <div className="crm-rr-dni-match crm-rr-dni-match--empty">
                            <div className="crm-rr-dni-match__avatar crm-rr-dni-match__avatar--warn" aria-hidden>!</div>
                            <div className="crm-rr-dni-match__info">
                              <div className="crm-rr-dni-match__name">Paciente no encontrado</div>
                              <div className="crm-rr-dni-match__meta">
                                DNI {formatDni(newPatientForm.dni)} — no está en Plex/Geclisa. En el siguiente paso vas a completar el alta.
                              </div>
                            </div>
                            <span className="crm-rr-dni-match__badge crm-rr-dni-match__badge--warn">Alta nueva</span>
                          </div>
                          <div className="crm-rr-dni-next-hint">
                            <span className="crm-rr-dni-next-hint__icon" aria-hidden>→</span>
                            <span>Continuá al paso <strong>Alta</strong> para registrar la ficha del paciente.</span>
                          </div>
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {newPatientStep === 'alta' ? (
              <div className="crm-alta-rapida-alta-step">
                <div className="crm-alta-rapida-alta-step__identity">
                  <div className="crm-alta-rapida-alta-step__avatar" aria-hidden>
                    <span>{(newPatientForm.fullName || '?').trim().charAt(0).toUpperCase() || '?'}</span>
                  </div>
                  <div className="crm-alta-rapida-alta-step__identity-copy">
                    <span className="crm-alta-rapida-alta-step__kicker">Nuevo registro</span>
                    <h3 className="crm-alta-rapida-alta-step__title">Alta de paciente</h3>
                    <p className="crm-alta-rapida-alta-step__subtitle">
                      Ficha en <strong>clientes externos</strong> · disponible para pedidos y bots
                    </p>
                  </div>
                  <div className="crm-alta-rapida-alta-step__dni-pill">
                    <span className="crm-alta-rapida-alta-step__dni-label">DNI</span>
                    <strong>{formatDni(newPatientForm.dni)}</strong>
                  </div>
                </div>

                <div className="crm-alta-rapida-alta-step__sections">
                  <section className="crm-alta-rapida-alta-section">
                    <header className="crm-alta-rapida-alta-section__head">
                      <span className="crm-alta-rapida-alta-section__icon" aria-hidden>👤</span>
                      <div>
                        <h4 className="crm-alta-rapida-alta-section__title">Identidad</h4>
                        <p className="crm-alta-rapida-alta-section__desc">Datos principales del paciente</p>
                      </div>
                    </header>
                    <div className="crm-alta-rapida-alta-section__body crm-alta-rapida-alta-section__body--grid">
                      <div className="crm-alta-rapida-field">
                        <label className="crm-alta-rapida-field__label">
                          Nombre y apellido <span className="crm-alta-rapida-field__req">*</span>
                        </label>
                        <input
                          className="crm-alta-rapida-field__input"
                          value={newPatientForm.fullName}
                          onChange={(e) => setNewPatientForm((prev) => ({ ...prev, fullName: e.target.value }))}
                          placeholder="Juan López"
                          autoComplete="name"
                        />
                      </div>
                      <div className="crm-alta-rapida-field">
                        <label className="crm-alta-rapida-field__label">
                          Fecha de nacimiento <span className="crm-alta-rapida-field__req">*</span>
                        </label>
                        <input
                          type="date"
                          className="crm-alta-rapida-field__input crm-alta-rapida-field__input--date"
                          value={newPatientForm.birthDate}
                          onChange={(e) => setNewPatientForm((prev) => ({ ...prev, birthDate: e.target.value }))}
                        />
                      </div>
                    </div>
                  </section>

                  <section className="crm-alta-rapida-alta-section">
                    <header className="crm-alta-rapida-alta-section__head">
                      <span className="crm-alta-rapida-alta-section__icon" aria-hidden>📱</span>
                      <div>
                        <h4 className="crm-alta-rapida-alta-section__title">Contacto</h4>
                        <p className="crm-alta-rapida-alta-section__desc">Para recordatorios y seguimiento</p>
                      </div>
                    </header>
                    <div className="crm-alta-rapida-alta-section__body crm-alta-rapida-alta-section__body--grid">
                      <div className="crm-alta-rapida-field">
                        <label className="crm-alta-rapida-field__label">
                          Teléfono <span className="crm-alta-rapida-field__req">*</span>
                        </label>
                        <input
                          type="tel"
                          inputMode="tel"
                          className="crm-alta-rapida-field__input"
                          value={newPatientForm.telefono}
                          onChange={(e) => setNewPatientForm((prev) => ({ ...prev, telefono: e.target.value.replace(/[^\d+\s()-]/g, '') }))}
                          placeholder="261 555 1234"
                        />
                      </div>
                      <div className="crm-alta-rapida-field">
                        <label className="crm-alta-rapida-field__label">
                          Email <span className="crm-alta-rapida-field__req">*</span>
                        </label>
                        <input
                          type="email"
                          className="crm-alta-rapida-field__input"
                          value={newPatientForm.email}
                          onChange={(e) => setNewPatientForm((prev) => ({ ...prev, email: e.target.value }))}
                          placeholder="correo@ejemplo.com"
                          autoComplete="email"
                        />
                      </div>
                    </div>
                  </section>

                  <section className="crm-alta-rapida-alta-section crm-alta-rapida-alta-section--coverage">
                    <header className="crm-alta-rapida-alta-section__head">
                      <span className="crm-alta-rapida-alta-section__icon" aria-hidden>🛡️</span>
                      <div>
                        <h4 className="crm-alta-rapida-alta-section__title">Cobertura</h4>
                        <p className="crm-alta-rapida-alta-section__desc">Obra social, afiliación y médico</p>
                      </div>
                    </header>
                    <div className="crm-alta-rapida-alta-section__body">
                      <div className="crm-alta-rapida-alta-section__body--grid">
                        <div className="crm-alta-rapida-field">
                          <label className="crm-alta-rapida-field__label">
                            Obra social <span className="crm-alta-rapida-field__req">*</span>
                          </label>
                          <button
                            type="button"
                            className="crm-gd-select"
                            disabled={obrasSocialesLoading}
                            style={{ ...styles.afilTrigger, ...(afilPicker === 'obra' ? styles.afilTriggerActive : null) }}
                            onClick={() => toggleAfilPicker('obra')}
                          >
                            <span style={newPatientForm.obraSocial ? styles.afilTriggerValue : styles.afilTriggerPlaceholder}>
                              {obrasSocialesLoading
                                ? 'Cargando obras sociales…'
                                : (newPatientForm.obraSocial || 'Seleccionar obra social')}
                            </span>
                            <span style={{ ...styles.afilCaret, ...(afilPicker === 'obra' ? styles.afilCaretOpen : null) }}>▾</span>
                          </button>
                        </div>
                        <div className="crm-alta-rapida-field">
                          <label className="crm-alta-rapida-field__label">
                            Médico de cabecera <span className="crm-alta-rapida-field__req">*</span>
                          </label>
                          <button
                            type="button"
                            className="crm-gd-select"
                            disabled={medicosLoading}
                            style={{ ...styles.afilTrigger, ...(afilPicker === 'medico' ? styles.afilTriggerActive : null) }}
                            onClick={() => toggleAfilPicker('medico')}
                          >
                            <span style={newPatientForm.medicoCabecera ? styles.afilTriggerValue : styles.afilTriggerPlaceholder}>
                              {medicosLoading
                                ? 'Cargando médicos…'
                                : (newPatientForm.medicoCabecera ? formatDoctorName(newPatientForm.medicoCabecera) : 'Seleccionar médico')}
                            </span>
                            <span style={{ ...styles.afilCaret, ...(afilPicker === 'medico' ? styles.afilCaretOpen : null) }}>▾</span>
                          </button>
                        </div>
                      </div>
                      <div style={styles.afilSlot}>
                        {afilPicker === 'obra' || afilPicker === 'medico' ? (
                          renderAfilDropdown(afilPicker)
                        ) : (
                          <div style={styles.afilSlotHint}>
                            <span style={styles.afilHintIcon}>🔎</span>
                            <span style={styles.afilHint}>Elegí una obra social o un médico para ver las opciones acá</span>
                          </div>
                        )}
                      </div>
                      <div className="crm-alta-rapida-field" style={{ marginTop: '2.1rem' }}>
                        <label className="crm-alta-rapida-field__label">
                          Nº afiliado <span className="crm-alta-rapida-field__req">*</span>
                        </label>
                        <input
                          className="crm-alta-rapida-field__input"
                          value={newPatientForm.nroAfiliado}
                          onChange={(e) => setNewPatientForm((prev) => ({ ...prev, nroAfiliado: e.target.value }))}
                          placeholder="Ej: 1234567/00"
                        />
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            ) : null}

            {newPatientStep === 'tratamiento' ? (
              <div style={styles.newPatientGrid}>
                <div>
                  <label className="crm-gd-field-label">Nombre comercial</label>
                  <input
                    className="crm-gd-input"
                    placeholder="Ej: Lipitor"
                    value={newPatientForm.commercialName}
                    onFocus={() => openMedSearch('newPatient', 'commercialName', newPatientForm.commercialName)}
                    onChange={(e) => {
                      setNewPatientForm((prev) => ({ ...prev, commercialName: e.target.value, medicamentoId: null }));
                      openMedSearch('newPatient', 'commercialName', e.target.value);
                    }}
                  />
                </div>
                <div>
                  <label className="crm-gd-field-label">Presentación</label>
                  <input
                    className="crm-gd-input"
                    placeholder="Se completa al elegir el medicamento"
                    value={newPatientForm.presentacion}
                    readOnly
                  />
                </div>
                <div style={styles.newTxSpan2}>
                  <div style={styles.afilPanel}>
                    {medSearchOpen && (medSearchField === 'newPatient:genericDrug' || medSearchField === 'newPatient:commercialName') ? (
                      <>
                        <div style={styles.afilPanelHead}>
                          <span style={styles.medPanelTitle}>
                            <span style={styles.medPanelIcon}>💊</span>
                            Resultados por nombre comercial
                          </span>
                          <span style={styles.afilCount}>{medSearchLoading ? '…' : medOptions.length}</span>
                        </div>
                        <div style={styles.afilList}>
                          {medSearchLoading ? (
                            <div style={styles.medPanelState}>
                              <span className="crm-gd-spinner" style={styles.miniSpinner} />
                              Buscando medicamentos…
                            </div>
                          ) : null}
                          {!medSearchLoading && medOptions.length === 0 ? (
                            <div style={styles.afilEmpty}>Sin resultados</div>
                          ) : null}
                          {!medSearchLoading && medOptions.slice(0, 12).map((med) => {
                            const parts = splitMedicamentoOption(med);
                            return (
                              <button
                                key={`np-med-${med.id}`}
                                type="button"
                                className="crm-rr-med-opt"
                                style={styles.medOptItem}
                                onClick={() => applyMedicationOption(med)}
                              >
                                <span style={styles.medOptPrimary}>{parts.primary}</span>
                                {parts.secondary ? <span style={styles.medOptSecondary}>{parts.secondary}</span> : null}
                              </button>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div style={styles.afilHintWrap}>
                        <span style={styles.afilHintIcon}>🔎</span>
                        <span style={styles.afilHint}>Escribí el nombre comercial para ver las opciones acá</span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={styles.newTxSpan2}>
                  <label className="crm-gd-field-label">Diagnóstico</label>
                  <input
                    className="crm-gd-input"
                    placeholder="Ej: Diabetes Mellitus tipo 2"
                    value={newPatientForm.diagnosis}
                    onChange={(e) => setNewPatientForm((prev) => ({ ...prev, diagnosis: e.target.value }))}
                  />
                </div>
                <div style={styles.newTxSpan2}>
                  <label className="crm-gd-field-label">Posología</label>
                  <input
                    className="crm-gd-input"
                    placeholder="Ej: 1 comprimido cada 8 horas"
                    value={newPatientForm.posologia}
                    onChange={(e) => setNewPatientForm((prev) => ({ ...prev, posologia: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="crm-gd-field-label">Fecha de inicio</label>
                  <input
                    type="date"
                    className="crm-gd-input"
                    value={newPatientForm.startDate}
                    onChange={(e) => setNewPatientForm((prev) => ({ ...prev, startDate: e.target.value }))}
                  />
                  <div className="crm-gd-card__meta" style={{ marginTop: 4 }}>dd/mm/aaaa</div>
                </div>
                <div>
                  <label className="crm-gd-field-label">Cantidad de meses</label>
                  <select
                    className="crm-gd-select"
                    value={newPatientForm.months}
                    onChange={(e) => setNewPatientForm((prev) => ({ ...prev, months: e.target.value }))}
                  >
                    <option value="1">1 mes</option>
                    <option value="3">3 meses</option>
                    <option value="6">6 meses</option>
                    <option value="12">12 meses</option>
                  </select>
                </div>
                <div>
                  <label className="crm-gd-field-label">Destino del pedido</label>
                  <select
                    className="crm-gd-select"
                    value={newPatientForm.destino}
                    onChange={(e) => setNewPatientForm((prev) => ({ ...prev, destino: e.target.value }))}
                  >
                    <option value="aprobacion_medica">Pendiente aprobación médica</option>
                    <option value="derivacion">Pendiente derivación</option>
                  </select>
                </div>
                {dniMatchFound ? (
                  <div>
                    <label className="crm-gd-field-label" style={styles.fieldLabelRow}>
                      <span>Médico de cabecera</span>
                      {medicosLoading ? (
                        <span style={styles.fieldLoading}>
                          <span className="crm-gd-spinner" style={styles.miniSpinner} />
                          Cargando…
                        </span>
                      ) : null}
                    </label>
                    <button
                      type="button"
                      className="crm-gd-select"
                      disabled={medicosLoading}
                      style={{ ...styles.afilTrigger, ...(afilPicker === 'medico' ? styles.afilTriggerActive : null) }}
                      onClick={() => { setMedSearchOpen(false); toggleAfilPicker('medico'); }}
                    >
                      <span style={newPatientForm.medicoCabecera ? styles.afilTriggerValue : styles.afilTriggerPlaceholder}>
                        {medicosLoading
                          ? 'Cargando médicos…'
                          : (newPatientForm.medicoCabecera ? formatDoctorName(newPatientForm.medicoCabecera) : 'Seleccionar médico')}
                      </span>
                      <span style={{ ...styles.afilCaret, ...(afilPicker === 'medico' ? styles.afilCaretOpen : null) }}>▾</span>
                    </button>
                  </div>
                ) : null}
                {dniMatchFound && afilPicker === 'medico' ? (
                  <div style={{ ...styles.newTxSpan2, height: 196 }}>
                    {renderAfilDropdown('medico')}
                  </div>
                ) : null}
              </div>
            ) : null}
            </div>

            <div style={styles.newPatientActions}>
              <button type="button" className="crm-gd-btn crm-gd-btn--ghost" onClick={closeNewPatientFlow} disabled={savingPatient || savingAltaPaciente}>
                Cancelar
              </button>
              {newPatientStep !== 'dni' ? (
                <button type="button" className="crm-gd-btn" onClick={goBackNewPatient} disabled={savingPatient || savingAltaPaciente}>
                  ← Atrás
                </button>
              ) : null}
              {newPatientStep === 'dni' ? (
                <button
                  type="button"
                  className="crm-gd-btn crm-gd-btn--primary"
                  onClick={goNextFromDni}
                  disabled={!isDniStepValid}
                  style={!isDniStepValid ? styles.btnDisabled : undefined}
                >
                  Siguiente →
                </button>
              ) : null}
              {newPatientStep === 'alta' ? (
                <button
                  type="button"
                  className="crm-gd-btn crm-gd-btn--primary"
                  onClick={registerAltaPaciente}
                  disabled={!isAltaValid || savingAltaPaciente}
                  style={(!isAltaValid || savingAltaPaciente) ? styles.btnDisabled : undefined}
                >
                  {savingAltaPaciente ? 'Guardando…' : 'Registrar y continuar →'}
                </button>
              ) : null}
              {newPatientStep === 'tratamiento' ? (
                <button
                  type="button"
                  className="crm-gd-btn crm-gd-btn--primary"
                  onClick={saveNewPatient}
                  disabled={!isTratamientoValid || savingPatient}
                  style={(!isTratamientoValid || savingPatient) ? styles.btnDisabled : undefined}
                >
                  {savingPatient ? 'Creando…' : 'Crear pedido'}
                </button>
              ) : null}
            </div>

            {savingPatient || savingAltaPaciente ? (
              <div className="crm-rr-loading-overlay" role="status" aria-live="polite">
                <div className="crm-rr-loading-card">
                  <div className="crm-rr-loading-spinner" aria-hidden>
                    <span className="crm-rr-loading-spinner__dot" />
                  </div>
                  <div className="crm-rr-loading-copy">
                    <div className="crm-rr-loading-title">
                      {savingAltaPaciente ? 'Registrando paciente' : 'Creando pedido'}
                      <span className="crm-rr-loading-dots" />
                    </div>
                    <div className="crm-rr-loading-sub">
                      {savingAltaPaciente
                        ? 'Guardando la ficha en clientes externos'
                        : 'Estamos cargando el tratamiento en la base'}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {emitModal ? (() => {
        const hasConversation = Boolean(emitModal.tx?.conversationId);
        const planCount = Array.isArray(emitModal.tx?.timeline) && emitModal.tx.timeline.length > 0
          ? emitModal.tx.timeline.length
          : resolveCantidadRecetas({
            cantidad_recetas: emitModal.tx?.cantidadRecetas,
            periodo_meses: emitModal.tx?.periodoMeses
          });
        return (
          <div style={styles.newPatientBackdrop} className="crm-rr-approve-backdrop">
            <div className="crm-rr-approve-modal crm-rr-approve-modal--emit" role="dialog" aria-modal="true" aria-labelledby="crm-rr-emit-title">
              <header className="crm-rr-approve-hero">
                <div className="crm-rr-approve-hero__main">
                  <span className="crm-rr-approve-hero__icon crm-rr-approve-hero__icon--emit" aria-hidden="true">📄</span>
                  <div className="crm-rr-approve-hero__copy">
                    <h3 id="crm-rr-emit-title" className="crm-rr-approve-hero__title">Registrar emisión</h3>
                    <p className="crm-rr-approve-hero__sub">
                      {emitModal.rx.label}
                      {emitModal.rx.monthLabel ? ` · ${emitModal.rx.monthLabel}` : ''}
                      {emitModal.rx.fechaPrevista ? ` · ${formatFechaCorta(emitModal.rx.fechaPrevista)}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="crm-rr-approve-close"
                  onClick={closeEmitModal}
                  disabled={confirmandoEmision}
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              </header>

              <nav className="crm-rr-approve-steps" aria-label="Pasos para registrar emisión">
                <button
                  type="button"
                  className={`crm-rr-approve-steps__item${emitStep === 'whatsapp' ? ' is-active' : ''}${emitStep === 'receta' ? ' is-done' : ''}`}
                  onClick={() => setEmitStep('whatsapp')}
                  disabled={confirmandoEmision}
                  aria-current={emitStep === 'whatsapp' ? 'step' : undefined}
                >
                  <span className="crm-rr-approve-steps__num">1</span>
                  <span>WhatsApp</span>
                </button>
                <span className="crm-rr-approve-steps__line" aria-hidden="true" />
                <button
                  type="button"
                  className={`crm-rr-approve-steps__item${emitStep === 'receta' ? ' is-active' : ''}`}
                  onClick={() => setEmitStep('receta')}
                  disabled={confirmandoEmision}
                  aria-current={emitStep === 'receta' ? 'step' : undefined}
                >
                  <span className="crm-rr-approve-steps__num">2</span>
                  <span>Receta emitida</span>
                </button>
              </nav>

              <div className="crm-rr-approve-body">
                {emitStep === 'whatsapp' ? (
                  <section className="crm-rr-approve-step crm-rr-approve-step--wa">
                    <div className="crm-rr-approve-section-head">
                      <span className="crm-rr-approve-section-head__icon" aria-hidden="true">💬</span>
                      <div>
                        <div className="crm-rr-approve-section-head__title">WhatsApp de emisión</div>
                        <p className="crm-rr-approve-section-head__hint">
                          Prepará el aviso al paciente antes de registrar la receta como emitida.
                        </p>
                      </div>
                    </div>

                    <div className="crm-rr-approve-wa-grid">
                      <button
                        type="button"
                        className={`crm-rr-approve-wa-card crm-rr-approve-wa-card--template${!hasConversation ? ' is-disabled' : ''}`}
                        onClick={() => hasConversation && openTemplatePicker(emitModal.tx.conversationId)}
                        disabled={!hasConversation || confirmandoEmision}
                      >
                        <span className="crm-rr-approve-wa-card__icon" aria-hidden="true">📋</span>
                        <span className="crm-rr-approve-wa-card__copy">
                          <strong>Usar plantilla aprobada</strong>
                          <small>
                            {hasConversation
                              ? 'Elegí una plantilla de WhatsApp y enviála antes de registrar.'
                              : 'Este pedido no tiene una conversación vinculada.'}
                          </small>
                        </span>
                        <span className="crm-rr-approve-wa-card__badge">
                          {hasConversation ? 'Disponible' : 'Sin chat'}
                        </span>
                      </button>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={emitWa}
                        aria-label="Enviar WhatsApp de emisión de receta al paciente"
                        className={`crm-rr-approve-wa-card crm-rr-approve-wa-card--switch${emitWa ? ' is-on' : ''}`}
                        onClick={() => setEmitWa((v) => !v)}
                        disabled={confirmandoEmision}
                      >
                        <span className="crm-rr-approve-wa-card__icon" aria-hidden="true">📄</span>
                        <span className="crm-rr-approve-wa-card__copy">
                          <strong>WhatsApp · Emitir receta</strong>
                          <small>
                            {planCount > 1
                              ? `Aviso por WhatsApp al paciente al registrar esta emisión (${planCount} recetas en el plan).`
                              : 'Aviso por WhatsApp al paciente al registrar esta emisión.'}
                          </small>
                        </span>
                        <span className={`crm-rr-approve-switch${emitWa ? ' is-on' : ''}`} aria-hidden="true">
                          <span className="crm-rr-approve-switch__knob" />
                        </span>
                        <span className={`crm-rr-approve-wa-card__state${emitWa ? ' is-on' : ''}`}>
                          {emitWa ? 'Activo' : 'Off'}
                        </span>
                      </button>
                    </div>

                    <div className="crm-rr-approve-note">
                      <strong>Paso 1</strong>
                      <span>La plantilla es manual; el switch activa el aviso automático cuando registres la emisión.</span>
                    </div>
                  </section>
                ) : (
                  <section className="crm-rr-approve-step crm-rr-approve-step--plan">
                    <div className="crm-rr-approve-plan-hero">
                      <span className="crm-rr-approve-plan-hero__icon" aria-hidden="true">✓</span>
                      <div>
                        <div className="crm-rr-approve-plan-hero__title">Registrar receta emitida</div>
                        <p className="crm-rr-approve-plan-hero__text">
                          Se marcará la receta como emitida en el sistema externo y quedará visible en el circuito de trazabilidad.
                        </p>
                      </div>
                    </div>

                    <div className="crm-rr-approve-fields">
                      <label className="crm-rr-approve-field">
                        <span className="crm-rr-approve-field__label">N° de receta (sistema externo)</span>
                        <input
                          className="crm-rr-approve-field__input"
                          value={emitirNro}
                          onChange={(e) => setEmitirNro(e.target.value)}
                          disabled={confirmandoEmision}
                        />
                      </label>
                      <label className="crm-rr-approve-field">
                        <span className="crm-rr-approve-field__label">URL de la receta (opcional)</span>
                        <input
                          type="url"
                          className="crm-rr-approve-field__input"
                          value={emitirUrl}
                          onChange={(e) => setEmitirUrl(e.target.value)}
                          placeholder="https://... (opcional)"
                          disabled={confirmandoEmision}
                        />
                      </label>
                    </div>

                    {normalizeText(emitirUrl) && !emitirUrlValid ? (
                      <div className="crm-rr-approve-note crm-rr-approve-note--error">
                        <strong>URL inválida</strong>
                        <span>Ingresá una URL válida comenzando con http:// o https://.</span>
                      </div>
                    ) : null}

                    <div className="crm-rr-approve-plan-grid">
                      <div className="crm-rr-approve-kv">
                        <span>Receta</span>
                        <strong>{emitModal.rx.label}</strong>
                      </div>
                      <div className="crm-rr-approve-kv">
                        <span>Fecha</span>
                        <strong>{emitModal.rx.fechaPrevista ? formatFechaCorta(emitModal.rx.fechaPrevista) : 'Sin fecha'}</strong>
                      </div>
                      <div className="crm-rr-approve-kv">
                        <span>Plan</span>
                        <strong>{planCount} receta{planCount > 1 ? 's' : ''}</strong>
                      </div>
                      <div className="crm-rr-approve-kv">
                        <span>WhatsApp</span>
                        <strong>{emitWa ? 'Activo' : 'Off'}</strong>
                      </div>
                    </div>

                    <ul className="crm-rr-approve-checklist" aria-label="Consecuencias de registrar la emisión">
                      <li>La receta queda marcada como emitida en el sistema externo.</li>
                      <li>Se actualiza la trazabilidad del tratamiento.</li>
                      <li>El equipo podrá continuar con la próxima receta del plan.</li>
                    </ul>
                  </section>
                )}
              </div>

              <footer className={`crm-rr-approve-footer${emitStep === 'receta' ? ' crm-rr-approve-footer--three' : ''}`}>
                <button
                  type="button"
                  className="crm-rr-approve-btn crm-rr-approve-btn--ghost"
                  onClick={closeEmitModal}
                  disabled={confirmandoEmision}
                >
                  Cancelar
                </button>
                {emitStep === 'receta' ? (
                  <button
                    type="button"
                    className="crm-rr-approve-btn crm-rr-approve-btn--ghost"
                    onClick={() => setEmitStep('whatsapp')}
                    disabled={confirmandoEmision}
                  >
                    Atrás
                  </button>
                ) : null}
                {emitStep === 'whatsapp' ? (
                  <button
                    type="button"
                    className="crm-rr-approve-btn crm-rr-approve-btn--primary"
                    onClick={() => setEmitStep('receta')}
                    disabled={confirmandoEmision}
                  >
                    Siguiente
                  </button>
                ) : (
                  <button
                    type="button"
                    className="crm-rr-approve-btn crm-rr-approve-btn--primary"
                    onClick={() => { void confirmarEmisionReceta(); }}
                    disabled={!canSubmitEmision}
                  >
                    {confirmandoEmision ? 'Registrando…' : 'Registrar como emitida'}
                  </button>
                )}
              </footer>
            </div>
          </div>
        );
      })() : null}

      {approveModal ? (() => {
        const approving = pedidoActionId === approveModal.id;
        const prescriptionCount = resolveCantidadRecetas({
          cantidad_recetas: approveModal.cantidadRecetas,
          periodo_meses: approveModal.periodoMeses
        });
        const hasConversation = Boolean(approveModal.conversationId);
        return (
          <div style={styles.newPatientBackdrop} className="crm-rr-approve-backdrop">
            <div className="crm-rr-approve-modal" role="dialog" aria-modal="true" aria-labelledby="crm-rr-approve-title">
              <header className="crm-rr-approve-hero">
                <div className="crm-rr-approve-hero__main">
                  <span className="crm-rr-approve-hero__icon" aria-hidden="true">✓</span>
                  <div className="crm-rr-approve-hero__copy">
                    <h3 id="crm-rr-approve-title" className="crm-rr-approve-hero__title">Aprobar tratamiento</h3>
                    <p className="crm-rr-approve-hero__sub">
                      {approveModal.medication}
                      {approveModal.diagnostico ? ` · ${approveModal.diagnostico}` : ''}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="crm-rr-approve-close"
                  onClick={closeApproveModal}
                  disabled={approving}
                  aria-label="Cerrar"
                >
                  ✕
                </button>
              </header>

              <nav className="crm-rr-approve-steps" aria-label="Pasos para aprobar tratamiento">
                <button
                  type="button"
                  className={`crm-rr-approve-steps__item${approveStep === 'whatsapp' ? ' is-active' : ''}${approveStep === 'plan' ? ' is-done' : ''}`}
                  onClick={() => setApproveStep('whatsapp')}
                  disabled={approving}
                  aria-current={approveStep === 'whatsapp' ? 'step' : undefined}
                >
                  <span className="crm-rr-approve-steps__num">1</span>
                  <span>WhatsApp</span>
                </button>
                <span className="crm-rr-approve-steps__line" aria-hidden="true" />
                <button
                  type="button"
                  className={`crm-rr-approve-steps__item${approveStep === 'plan' ? ' is-active' : ''}`}
                  onClick={() => setApproveStep('plan')}
                  disabled={approving}
                  aria-current={approveStep === 'plan' ? 'step' : undefined}
                >
                  <span className="crm-rr-approve-steps__num">2</span>
                  <span>Confirmar plan</span>
                </button>
              </nav>

              <div className="crm-rr-approve-body">
                {approveStep === 'whatsapp' ? (
                  <section className="crm-rr-approve-step crm-rr-approve-step--wa">
                    <div className="crm-rr-approve-section-head">
                      <span className="crm-rr-approve-section-head__icon" aria-hidden="true">💬</span>
                      <div>
                        <div className="crm-rr-approve-section-head__title">WhatsApp al paciente</div>
                        <p className="crm-rr-approve-section-head__hint">
                          Definí la comunicación antes de generar el plan de prescripción.
                        </p>
                      </div>
                    </div>

                    <div className="crm-rr-approve-wa-grid">
                      <button
                        type="button"
                        className={`crm-rr-approve-wa-card crm-rr-approve-wa-card--template${!hasConversation ? ' is-disabled' : ''}`}
                        onClick={() => hasConversation && openTemplatePicker(approveModal.conversationId)}
                        disabled={!hasConversation || approving}
                      >
                        <span className="crm-rr-approve-wa-card__icon" aria-hidden="true">📋</span>
                        <span className="crm-rr-approve-wa-card__copy">
                          <strong>Usar plantilla aprobada</strong>
                          <small>
                            {hasConversation
                              ? 'Elegí una plantilla de WhatsApp y enviála antes de aprobar.'
                              : 'Este pedido no tiene una conversación vinculada.'}
                          </small>
                        </span>
                        <span className="crm-rr-approve-wa-card__badge">
                          {hasConversation ? 'Disponible' : 'Sin chat'}
                        </span>
                      </button>

                      <button
                        type="button"
                        role="switch"
                        aria-checked={approveWa}
                        aria-label="Enviar WhatsApp de alta de tratamiento al aprobar"
                        className={`crm-rr-approve-wa-card crm-rr-approve-wa-card--switch${approveWa ? ' is-on' : ''}`}
                        onClick={() => setApproveWa((v) => !v)}
                        disabled={approving}
                      >
                        <span className="crm-rr-approve-wa-card__icon" aria-hidden="true">💬</span>
                        <span className="crm-rr-approve-wa-card__copy">
                          <strong>WhatsApp · Alta tratamiento</strong>
                          <small>Mensaje automático con posología, período y recetas.</small>
                        </span>
                        <span className={`crm-rr-approve-switch${approveWa ? ' is-on' : ''}`} aria-hidden="true">
                          <span className="crm-rr-approve-switch__knob" />
                        </span>
                        <span className={`crm-rr-approve-wa-card__state${approveWa ? ' is-on' : ''}`}>
                          {approveWa ? 'Activo' : 'Off'}
                        </span>
                      </button>
                    </div>

                    <div className="crm-rr-approve-note">
                      <strong>Paso 1</strong>
                      <span>La plantilla es manual; el switch activa el webhook automático al aprobar.</span>
                    </div>
                  </section>
                ) : (
                  <section className="crm-rr-approve-step crm-rr-approve-step--plan">
                    <div className="crm-rr-approve-plan-hero">
                      <span className="crm-rr-approve-plan-hero__icon" aria-hidden="true">📄</span>
                      <div>
                        <div className="crm-rr-approve-plan-hero__title">Generar plan de prescripción</div>
                        <p className="crm-rr-approve-plan-hero__text">
                          Se aprobará el tratamiento y quedará listo para emitir recetas desde el circuito operativo.
                        </p>
                      </div>
                    </div>

                    <div className="crm-rr-approve-plan-grid">
                      <div className="crm-rr-approve-kv">
                        <span>Recetas</span>
                        <strong>{prescriptionCount}</strong>
                      </div>
                      <div className="crm-rr-approve-kv">
                        <span>Inicio</span>
                        <strong>{approveModal.fechaInicio ? formatFechaCorta(approveModal.fechaInicio) : 'Hoy'}</strong>
                      </div>
                      <div className="crm-rr-approve-kv">
                        <span>Médico</span>
                        <strong>{normalizeText(approveModal.medico) || 'Sin asignar'}</strong>
                      </div>
                      <div className="crm-rr-approve-kv">
                        <span>WhatsApp</span>
                        <strong>{approveWa ? 'Activo' : 'Off'}</strong>
                      </div>
                    </div>

                    <ul className="crm-rr-approve-checklist" aria-label="Consecuencias de aprobación">
                      <li>El tratamiento cambia a aprobado.</li>
                      <li>Se crea el plan de {prescriptionCount} receta{prescriptionCount > 1 ? 's' : ''}.</li>
                      <li>Queda disponible para emisión y trazabilidad.</li>
                    </ul>
                  </section>
                )}
              </div>

              <footer className={`crm-rr-approve-footer${approveStep === 'plan' ? ' crm-rr-approve-footer--three' : ''}`}>
                <button
                  type="button"
                  className="crm-rr-approve-btn crm-rr-approve-btn--ghost"
                  onClick={closeApproveModal}
                  disabled={approving}
                >
                  Cancelar
                </button>
                {approveStep === 'plan' ? (
                  <button
                    type="button"
                    className="crm-rr-approve-btn crm-rr-approve-btn--ghost"
                    onClick={() => setApproveStep('whatsapp')}
                    disabled={approving}
                  >
                    Atrás
                  </button>
                ) : null}
                {approveStep === 'whatsapp' ? (
                  <button
                    type="button"
                    className="crm-rr-approve-btn crm-rr-approve-btn--primary"
                    onClick={() => setApproveStep('plan')}
                    disabled={approving}
                  >
                    Siguiente
                  </button>
                ) : (
                  <button
                    type="button"
                    className="crm-rr-approve-btn crm-rr-approve-btn--primary"
                    onClick={() => { void runApproveFlow(approveModal); }}
                    disabled={approving}
                  >
                    {approving ? 'Aprobando…' : 'Aprobar tratamiento'}
                  </button>
                )}
              </footer>
            </div>
          </div>
        );
      })() : null}

      {templateModalOpen ? (
        <div
          className="crm-cw-tpl-overlay"
          role="dialog"
          aria-label="Enviar plantilla de WhatsApp"
          onClick={(e) => {
            if (e.target === e.currentTarget && !sendingTemplate) closeTemplateModal();
          }}
        >
          <div className="crm-cw-tpl-modal">
            <div className="crm-cw-tpl-head">
              <div className="crm-cw-tpl-head__title">
                <span aria-hidden="true">📋</span>
                <strong>Plantillas de WhatsApp</strong>
              </div>
              <button
                type="button"
                className="crm-cw-tpl-close"
                onClick={closeTemplateModal}
                disabled={sendingTemplate}
                aria-label="Cerrar"
              >
                ✕
              </button>
            </div>
            <p className="crm-cw-tpl-sub">
              Elegí una plantilla aprobada para enviar al paciente.
            </p>

            {templatesLoading ? (
              <div className="crm-cw-tpl-loading">Cargando plantillas…</div>
            ) : templatesError && !selectedTemplate ? (
              <div className="crm-cw-tpl-error">{templatesError}</div>
            ) : !selectedTemplate ? (
              templates.length === 0 ? (
                <div className="crm-cw-tpl-empty">
                  No hay plantillas aprobadas en este inbox de WhatsApp.
                </div>
              ) : (
                <div className="crm-cw-tpl-list">
                  {templates
                    .filter(
                      (tpl) =>
                        tpl.name === 'saludo_inicial'
                    )
                    .map((tpl) => (
                    <button
                      key={`${tpl.name}-${tpl.language}`}
                      type="button"
                      className="crm-cw-tpl-item"
                      onClick={() => handlePickTemplate(tpl)}
                    >
                      <div className="crm-cw-tpl-item__head">
                        <strong>{tpl.name}</strong>
                        <span className="crm-cw-tpl-item__lang">{tpl.language}</span>
                        {tpl.category ? (
                          <span className="crm-cw-tpl-item__cat">{tpl.category}</span>
                        ) : null}
                      </div>
                      <span className="crm-cw-tpl-item__body">{tpl.body || '(sin cuerpo)'}</span>
                      {tpl.params_count > 0 ? (
                        <span className="crm-cw-tpl-item__params">
                          {tpl.params_count} variable{tpl.params_count > 1 ? 's' : ''} para completar
                        </span>
                      ) : null}
                    </button>
                  ))}
                </div>
              )
            ) : (
              <div className="crm-cw-tpl-detail">
                <button
                  type="button"
                  className="crm-cw-tpl-back"
                  onClick={() => {
                    setSelectedTemplate(null);
                    setTemplateParamValues([]);
                    setTemplatesError('');
                  }}
                  disabled={sendingTemplate}
                >
                  ← Volver a la lista
                </button>
                <div className="crm-cw-tpl-detail__name">
                  <strong>{selectedTemplate.name}</strong>
                  <span className="crm-cw-tpl-item__lang">{selectedTemplate.language}</span>
                </div>
                {selectedTemplate.header_text ? (
                  <div className="crm-cw-tpl-preview crm-cw-tpl-preview--header">
                    {selectedTemplate.header_text}
                  </div>
                ) : null}
                <div className="crm-cw-tpl-preview">
                  {fillTemplateBody(selectedTemplate.body, templateParamValues)}
                </div>
                {selectedTemplate.footer_text ? (
                  <div className="crm-cw-tpl-footer">{selectedTemplate.footer_text}</div>
                ) : null}

                {selectedTemplate.params_count > 0 ? (
                  <div className="crm-cw-tpl-params">
                    {Array.from({ length: selectedTemplate.params_count }).map((_, idx) => (
                      <label key={idx} className="crm-cw-tpl-param">
                        <span>Variable {`{{${idx + 1}}}`}</span>
                        <input
                          type="text"
                          value={templateParamValues[idx] || ''}
                          placeholder={`Valor para {{${idx + 1}}}`}
                          onChange={(e) => {
                            const next = [...templateParamValues];
                            next[idx] = e.target.value;
                            setTemplateParamValues(next);
                          }}
                        />
                      </label>
                    ))}
                  </div>
                ) : null}

                {templatesError ? (
                  <div className="crm-cw-tpl-error">{templatesError}</div>
                ) : null}

                <button
                  type="button"
                  className="crm-cw-tpl-send"
                  onClick={handleSendTemplate}
                  disabled={sendingTemplate}
                >
                  {sendingTemplate ? 'Enviando…' : 'Enviar plantilla'}
                </button>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}

const styles = {
  page: {
    gap: 8,
    minHeight: 0,
    position: 'relative'
  },
  loadingOverlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 60,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    background: 'rgba(248,250,252,.78)',
    backdropFilter: 'blur(4px)',
    borderRadius: 16
  },
  loadingSpinner: {
    width: 46,
    height: 46,
    borderWidth: 4
  },
  kpiGridCompact: {
    gap: 8,
    marginBottom: 2
  },
  kpiCardCompact: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: 2,
    padding: '8px 14px',
    minHeight: 0
  },
  metaBadge: {
    border: '1px solid #cbd5e1',
    borderRadius: 999,
    padding: '6px 10px',
    fontWeight: 700,
    color: '#334155',
    background: '#f8fafc',
    fontSize: 12
  },
  layout: {
    display: 'grid',
    gridTemplateColumns: '260px minmax(0,1fr)',
    gap: 12,
    minHeight: 560,
    height: 'calc(100dvh - 290px)'
  },
  sidebar: {
    border: '1px solid rgba(148,163,184,.22)',
    borderRadius: 16,
    background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
    padding: 8,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0
  },
  sidebarHead: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 2,
    marginBottom: 8
  },
  sidebarTitle: {
    color: '#0f172a',
    fontSize: 15,
    fontWeight: 800
  },
  sidebarSubtitle: {
    color: '#64748b',
    fontSize: 12,
    fontWeight: 600
  },
  metaMuted: {
    color: '#64748b',
    fontSize: 12,
    fontWeight: 600
  },
  patientList: {
    display: 'grid',
    gap: 8,
    overflowY: 'auto',
    paddingRight: 2,
    minHeight: 0
  },
  patientItem: {
    border: '1px solid rgba(148,163,184,.24)',
    borderRadius: 12,
    background: '#fff',
    textAlign: 'left',
    padding: '8px 10px',
    cursor: 'pointer'
  },
  patientNameCompact: {
    fontSize: 13
  },
  patientTitleCompact: {
    fontSize: 15
  },
  patientItemActive: {
    borderColor: 'rgba(37,99,235,.4)',
    background: 'linear-gradient(180deg,#eff6ff,#f8fbff)'
  },
  patientBadges: {
    marginTop: 6,
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap'
  },
  detailPane: {
    minWidth: 0,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: 2
  },
  emptyState: {
    height: '100%',
    minHeight: 320,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    gap: 6,
    border: '1px dashed rgba(148,163,184,.45)',
    borderRadius: 16,
    background: 'linear-gradient(180deg,#ffffff,#f8fbff)'
  },
  emptyIcon: {
    fontSize: 40,
    opacity: 0.7,
    marginBottom: 4
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 800,
    color: '#0f172a',
    letterSpacing: '-.01em'
  },
  emptyText: {
    fontSize: 13.5,
    color: '#64748b',
    fontWeight: 600
  },
  actionsRow: {
    marginTop: 8,
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  },
  txTitleCompact: {
    fontSize: 13
  },
  linkBtn: {
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center'
  },
  editGrid: {
    marginTop: 10,
    display: 'grid',
    gap: 8
  },
  timelineWrap: {
    marginTop: 10,
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
    alignItems: 'center'
  },
  rxPill: {
    borderRadius: 999,
    border: '1px solid transparent',
    padding: '6px 10px',
    fontSize: 11,
    fontWeight: 800,
    cursor: 'pointer'
  },
  rxDone: {
    background: '#ecfdf3',
    borderColor: '#86efac',
    color: '#166534'
  },
  rxPending: {
    background: '#fffbeb',
    borderColor: '#fcd34d',
    color: '#b45309'
  },
  newTxCard: {
    marginTop: 8,
    padding: 12
  },
  newTxTriggerWrap: {
    marginTop: 8,
    marginBottom: 2,
    display: 'flex'
  },
  newTxTriggerSubtle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    border: '1px dashed rgba(148,163,184,.6)',
    background: 'rgba(248,250,252,.7)',
    color: '#475569',
    borderRadius: 10,
    padding: '8px 14px',
    fontSize: 12.5,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all .15s ease'
  },
  newTxTriggerPlus: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 18,
    height: 18,
    borderRadius: 999,
    background: '#2563eb',
    color: '#fff',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1
  },
  drawerBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 2700,
    background: 'rgba(15,23,42,.42)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    justifyContent: 'flex-end',
    transition: 'opacity .28s ease'
  },
  drawerBackdropOpen: {
    opacity: 1,
    pointerEvents: 'auto'
  },
  drawerBackdropClosed: {
    opacity: 0,
    pointerEvents: 'none'
  },
  drawerPanel: {
    width: 'min(520px, 96vw)',
    height: '100%',
    background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
    borderLeft: '1px solid rgba(148,180,220,.28)',
    boxShadow: '-24px 0 56px rgba(15,40,72,.2)',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform .32s cubic-bezier(.22,1,.36,1)'
  },
  drawerPanelOpen: {
    transform: 'translateX(0)'
  },
  drawerPanelClosed: {
    transform: 'translateX(100%)'
  },
  drawerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    padding: '14px 18px',
    borderBottom: '1px solid rgba(148,180,220,.18)'
  },
  drawerTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: '#0f2744',
    letterSpacing: '-.02em',
    lineHeight: 1.2
  },
  drawerSub: {
    marginTop: 3,
    fontSize: 12,
    color: '#64748b',
    fontWeight: 600,
    lineHeight: 1.4
  },
  drawerBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 18px'
  },
  drawerGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 11
  },
  drawerFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    padding: '12px 18px',
    borderTop: '1px solid rgba(148,180,220,.18)'
  },
  newTxGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 8,
    marginTop: 8
  },
  newTxSpan2: {
    gridColumn: '1 / -1'
  },
  newTxActions: {
    marginTop: 10,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    flexWrap: 'wrap'
  },
  medSearchCell: {
    position: 'relative'
  },
  medSearchPanel: {
    position: 'absolute',
    top: 'calc(100% + 4px)',
    left: 0,
    right: 0,
    zIndex: 25,
    background: '#fff',
    border: '1px solid rgba(148,163,184,.32)',
    borderRadius: 10,
    boxShadow: '0 12px 24px rgba(15,23,42,.12)',
    maxHeight: 220,
    overflowY: 'auto',
    padding: 6
  },
  medSearchItem: {
    width: '100%',
    textAlign: 'left',
    border: 'none',
    borderRadius: 8,
    background: '#fff',
    padding: '8px 9px',
    fontSize: 12,
    color: '#0f172a',
    cursor: 'pointer'
  },
  medSearchEmpty: {
    fontSize: 12,
    color: '#64748b',
    padding: '8px 9px'
  },
  fieldLabelRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8
  },
  fieldLoading: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 11,
    fontWeight: 700,
    color: '#2563eb'
  },
  miniSpinner: {
    width: 12,
    height: 12,
    borderWidth: 2
  },
  selectWrap: {
    position: 'relative'
  },
  selectConstrained: {
    maxWidth: 340
  },
  afilTrigger: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    width: '100%',
    cursor: 'pointer',
    textAlign: 'left'
  },
  afilTriggerActive: {
    borderColor: 'rgba(37,99,235,.55)',
    boxShadow: '0 0 0 3px rgba(37,99,235,.14)'
  },
  afilTriggerValue: {
    color: '#0f172a',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  afilTriggerPlaceholder: {
    color: '#94a3b8',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  afilCaret: {
    color: '#64748b',
    fontSize: 12,
    transition: 'transform .18s ease',
    flexShrink: 0
  },
  afilCaretOpen: {
    transform: 'rotate(180deg)'
  },
  afilPanel: {
    marginTop: 4,
    height: 196,
    border: '1px solid rgba(148,180,220,.28)',
    borderRadius: 12,
    background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,.6)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden'
  },
  afilPanelHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 9,
    borderBottom: '1px solid rgba(148,180,220,.16)'
  },
  afilSearch: {
    flex: 1
  },
  afilCount: {
    flexShrink: 0,
    minWidth: 28,
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 800,
    color: '#1d4ed8',
    background: '#eff6ff',
    border: '1px solid rgba(37,99,235,.25)',
    borderRadius: 999,
    padding: '3px 8px'
  },
  afilList: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 2
  },
  afilItem: {
    width: '100%',
    textAlign: 'left',
    border: 'none',
    borderRadius: 8,
    background: '#fff',
    padding: '8px 10px',
    fontSize: 12.5,
    color: '#0f2744',
    fontWeight: 600,
    cursor: 'pointer'
  },
  afilItemActive: {
    background: '#eff6ff',
    color: '#1d4ed8'
  },
  medPanelTitle: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12,
    fontWeight: 800,
    color: '#334155',
    letterSpacing: 0.1
  },
  medPanelIcon: {
    fontSize: 14
  },
  medPanelState: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12.5,
    color: '#64748b',
    fontWeight: 600,
    padding: 12
  },
  medOptItem: {
    width: '100%',
    textAlign: 'left',
    border: '1px solid transparent',
    borderRadius: 9,
    background: '#fff',
    padding: '8px 10px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    transition: 'background .12s ease, border-color .12s ease'
  },
  medOptPrimary: {
    fontSize: 12.5,
    fontWeight: 700,
    color: '#0f2744',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  medOptSecondary: {
    fontSize: 11,
    fontWeight: 600,
    color: '#64748b',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  afilEmpty: {
    fontSize: 12,
    color: '#64748b',
    padding: 12,
    textAlign: 'center'
  },
  afilHintWrap: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16
  },
  afilSlot: {
    marginTop: 8,
    height: 196
  },
  afilSlotHint: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    borderRadius: 12,
    border: '1px dashed rgba(148,180,220,.45)',
    background: 'linear-gradient(180deg,#ffffff,#f8fbff)'
  },
  afilHintIcon: {
    fontSize: 20,
    opacity: 0.55
  },
  afilHint: {
    fontSize: 12.5,
    color: '#94a3b8',
    fontWeight: 600,
    textAlign: 'center',
    maxWidth: 280
  },
  selectLoading: {
    color: '#94a3b8',
    paddingRight: 34
  },
  selectSpinner: {
    position: 'absolute',
    top: '50%',
    right: 12,
    width: 16,
    height: 16,
    borderWidth: 2,
    marginTop: -8,
    pointerEvents: 'none'
  },
  medSearchHint: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: '#94a3b8',
    padding: '4px 9px 6px'
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    boxShadow: 'none'
  },
  newPatientBackdrop: {
    position: 'fixed',
    inset: 0,
    zIndex: 2400,
    background: 'rgba(15,23,42,.42)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16
  },
  newPatientModal: {
    position: 'relative',
    width: 'min(620px, 96vw)',
    maxHeight: '92vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: 'linear-gradient(180deg,#ffffff,#f8fbff)',
    border: '1px solid rgba(148,180,220,.28)',
    borderRadius: 18,
    boxShadow: '0 28px 60px rgba(15,40,72,.22)'
  },
  newPatientBody: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '14px 18px'
  },
  newPatientHeader: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    padding: '15px 18px 12px',
    borderBottom: '1px solid rgba(148,180,220,.18)'
  },
  newPatientTitle: {
    fontSize: 16,
    fontWeight: 900,
    color: '#0f2744',
    letterSpacing: '-.02em',
    lineHeight: 1.2
  },
  newPatientSub: {
    marginTop: 3,
    fontSize: 12,
    color: '#64748b',
    fontWeight: 600,
    lineHeight: 1.4
  },
  newPatientSteps: {
    display: 'flex',
    gap: 6,
    marginBottom: 12
  },
  stepPill: {
    flex: 1,
    textAlign: 'center',
    border: '1px solid rgba(148,180,220,.32)',
    borderRadius: 10,
    padding: '7px 12px',
    fontSize: 11.5,
    color: '#475569',
    fontWeight: 800,
    background: '#fff'
  },
  stepPillActive: {
    borderColor: 'rgba(35,101,171,.45)',
    color: '#fff',
    background: 'linear-gradient(135deg,#3b82f6 0%,#2365ab 100%)',
    boxShadow: '0 8px 18px rgba(35,101,171,.22)'
  },
  stepPillDone: {
    borderColor: 'rgba(34,197,94,.4)',
    color: '#15803d',
    background: 'rgba(34,197,94,.1)'
  },
  newPatientGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 11
  },
  newPatientActions: {
    flexShrink: 0,
    padding: '12px 18px',
    borderTop: '1px solid rgba(148,180,220,.18)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
    flexWrap: 'wrap',
    background: 'rgba(255,255,255,.7)'
  }
};
